import React, { useState, useEffect, useCallback, useRef } from "react"
import { Rnd } from 'react-rnd'
import "./App.css"

// Theme and styling
import { ThemeProvider } from "@mui/material/styles"
import { CssBaseline } from "@mui/material"
import { getTheme, DEFAULT_THEME } from "./theme"

// Components
import AuthScreen from "./components/AuthScreen"
import TopBar from "./components/TopBar"
import VideoGrid from "./components/VideoGrid"
import ChatPanel from "./components/ChatPanel"
import RightSidebar from "./components/RightSidebar"
import UserContextMenu, { enrichUser } from "./components/UserContextMenu"
import { ComposerProvider } from "./components/composer/ComposerContext"
import ReconnectModal from "./components/ReconnectModal"
import KickedModal from "./components/KickedModal"
import DeviceSetupModal from "./components/DeviceSetupModal"
import ProfileCard from "./components/ProfileCard"
import VolumePopover from "./components/VolumePopover"

// API Services
import {
  authAPI,
  authStorage,
  getTenantInfoFromURL,
  messagesAPI,
  uploadAPI,
  APIError,
} from "./services/api"

// Socket Service
import socketService from "./services/socketService"
// Media (WebRTC SFU) service
import mediaService from "./services/mediaService"
// Publisher-side vocal chain (mic → Web Audio graph → published track)
import voiceChain, { DEFAULT_MIC_PROCESSING } from "./services/voiceChain"
// Consumer-side per-user listening chains (volume boost / compressor / limiter)
import remoteAudio from "./services/remoteAudio"
// Platform detection (iOS single-camera special-casing)
import { isIOS } from "./utils/platform"
import { checkForNewBuild } from "./utils/staleBundle"

// Mention notification sound + detection helpers.
import { playMentionSound, DEFAULT_MENTION_SOUND, MENTION_SOUNDS } from "./utils/notifySound"
import {
  FONT_SIZES,
  DEFAULT_FONT_SIZE,
  TIMESTAMP_FORMATS,
  DEFAULT_TIMESTAMP_FORMAT,
  DEFAULT_MENTION_VOLUME,
  clampVolume,
} from "./utils/displayPrefs"
import { containsMentionOf, wireToPlainText } from "./utils/richText"
import { playImSound } from "./utils/imSounds"
import { canUseCap, canUseCapWith } from "./utils/guestCaps"
import useActivityPing from "./hooks/useActivityPing"
import IdleWarningBanner from "./components/IdleWarningBanner"
import IdleKickedScreen from "./components/IdleKickedScreen"
import ImConversation from "./components/ImConversation"
import { WindowManagerProvider, useWindowManager, useWindowState } from "./windowing/WindowManagerContext"
import WindowLayer from "./windowing/WindowLayer"

// ReflexContainer for resizable layout
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex"
import "react-reflex/styles.css"
import VideoParticipantTile from './components/VideoParticipantTile'

// Server-stored quick-reply text is sanitizeContent output (entity-encoded).
// Decode every list on its way into state so the UI works in plain text.
const decodeQuickReplies = (list) =>
  (list || []).map((r) => ({ ...r, text: wireToPlainText(r.text) }))

// Auto-reconnect backoff schedule (ms before each attempt). 5 attempts:
// immediate, then 5s, 10s, 30s, 60s. After the last one fails, the modal
// switches to a manual Retry / Leave state (Retry does ONE more attempt and
// does not reset the counter).
const RECONNECT_DELAYS = [0, 5000, 10000, 30000, 60000]

// When the drop is a KNOWN server restart (deploy), the backend needs a few
// seconds to come back. Firing the first attempt at 0ms (as for a network blip)
// just wastes it on a refused connection, so we wait this long before the first
// maintenance reconnect attempt, then fall into the normal backoff.
const MAINTENANCE_FIRST_DELAY_MS = 3000

// localStorage key for the user's chosen visual theme ("midnight" | "sesh").
const THEME_STORAGE_KEY = "chatTheme"

// localStorage key for the mention-notification sound ("chime" | "off" | ...).
const MENTION_SOUND_STORAGE_KEY = "chatMentionSound"

// Max size for a peer-to-peer file transfer. Mirrors the backend's
// MAX_FILE_BYTES (25 MB) so oversized files are rejected client-side before an
// offer is ever sent. Files are relayed in memory, never stored server-side.
const FILE_TRANSFER_MAX_BYTES = 25 * 1024 * 1024

// localStorage keys for the chat display prefs (mention volume 0-100, message
// font size, timestamp format). Like the theme/sound mirrors, these seed the
// UI before auth loads; the membership values then hydrate them.
const MENTION_VOLUME_STORAGE_KEY = "chatMentionVolume"
const FONT_SIZE_STORAGE_KEY = "chatFontSize"
const TIMESTAMP_FORMAT_STORAGE_KEY = "chatTimestampFormat"

// localStorage key for IM mode — the AIM-style private-chat toggle. When ON,
// private messages open a draggable IM window per conversation; when OFF, they
// fall back to inline system notices in the main chat. A pure client display
// preference (no backend pref), so it lives in localStorage like theme/font.
const IM_MODE_STORAGE_KEY = "chatImMode"

// Retro "classic" IM skin (AOL-Instant-Messenger look) on/off. A pure client
// display preference like IM mode itself; persisted to localStorage. When on,
// every IM window renders the classic skin, OVERRIDING the app theme. Default
// OFF so the themed skin remains the out-of-box look.
const IM_CLASSIC_STORAGE_KEY = "chatImClassic"

// AIM message sounds (the real Send/Recv MP3 samples) on/off — toggled from the
// classic IM window's Edit menu. Pure client pref; persisted to localStorage.
// Default ON (the retro sounds are part of the AIM experience).
const IM_SOUNDS_STORAGE_KEY = "chatImSounds"

// Cap on how many message lines we keep per conversation in memory. Private
// messages are never DB-stored; this just bounds the in-tab buffers.
const IM_MAX_LINES = 100

// Cap on the live room transcript kept in memory. Large enough that the message
// list overflows its container and scrolls (the initial history load can return
// well over a screenful); bounded so a long-running session doesn't grow the
// array without limit.
const MAX_MESSAGES = 300

// How long the client keeps the "Take a hit!" banner up after sesh:done before
// returning to idle. Slightly longer than the server's HIT window (5s) so we
// never clear a frame early; the server has already deleted the sesh by then
// and sends no further events, so this local timeout owns the idle transition.
const SESH_HIT_CLEAR_MS = 5500

// Watches the window manager's focused window and reports changes up to App so
// it can clear a conversation's unread/blink when its window is focused. Renders
// nothing; it just subscribes to window state (which App itself deliberately
// does not, to avoid re-rendering on every drag/focus).
function ImFocusBridge({ onFocusedChange }) {
  const { focusedId } = useWindowState()
  useEffect(() => {
    onFocusedChange(focusedId)
  }, [focusedId, onFocusedChange])
  return null
}

function AppInner() {
  const [currentRoom] = useState("general")
  // Active visual theme name. Resolved (in priority order) from: the user's
  // own saved override (membership.theme) → the tenant default
  // (tenantConfig.ui.defaultTheme) → the localStorage mirror → DEFAULT_THEME.
  // Initialized from localStorage so there's no flash before auth/config load;
  // the server/tenant values then hydrate it (see effect below). Switching
  // repaints the whole app (see theme.js).
  const [themeName, setThemeName] = useState(() => {
    try {
      return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME
    } catch {
      return DEFAULT_THEME
    }
  })
  // Once the user explicitly picks a theme this session, stop letting the
  // server/tenant hydration override their live choice.
  const themePickedThisSession = useRef(false)
  const theme = getTheme(themeName)
  const handleThemeChange = useCallback((name) => {
    themePickedThisSession.current = true
    setThemeName(name)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, name)
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
    // Persist as a per-user override on the membership (best-effort; the local
    // apply + localStorage mirror above already took effect for this session).
    socketService.setTheme(name)
  }, [])
  // Paint the document body with the active theme's backdrop. The sesh theme's
  // backdrop is layered radial glows that should bleed behind the whole app
  // (the .app-root sits on top with its own surface), so we set it on <body>
  // rather than only the app container.
  useEffect(() => {
    const prev = document.body.style.background
    document.body.style.background = theme.palette.custom.bodyBackdrop
    return () => {
      document.body.style.background = prev
    }
  }, [theme])
  // Mention-notification sound preference ("chime" | "off"). Resolved like the
  // theme: own saved override (membership.mentionSound) → localStorage mirror →
  // default. Initialized from localStorage so it's correct before auth loads;
  // the membership value then hydrates it (effect below).
  const [mentionSound, setMentionSound] = useState(() => {
    try {
      const saved = localStorage.getItem(MENTION_SOUND_STORAGE_KEY)
      return MENTION_SOUNDS.includes(saved) ? saved : DEFAULT_MENTION_SOUND
    } catch {
      return DEFAULT_MENTION_SOUND
    }
  })
  // Latest value readable inside once-registered socket listeners (onNewMessage
  // is registered once; it must read the CURRENT preference, not a stale one).
  const mentionSoundRef = useRef(mentionSound)
  mentionSoundRef.current = mentionSound
  // Once the user picks a sound this session, a late membership hydration must
  // not yank it back (mirrors themePickedThisSession).
  const mentionSoundPickedThisSession = useRef(false)
  const handleMentionSoundChange = useCallback((value) => {
    if (!MENTION_SOUNDS.includes(value)) return
    mentionSoundPickedThisSession.current = true
    setMentionSound(value)
    try {
      localStorage.setItem(MENTION_SOUND_STORAGE_KEY, value)
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
    // Persist as a per-user override on the membership (best-effort).
    socketService.setMentionSound(value)
    // Preview the choice on change so the user hears what they picked.
    playMentionSound(value, mentionVolumeRef.current)
  }, [])

  // Mention-sound VOLUME (0-100). Resolved like the sound itself: own saved
  // override (membership.mentionVolume) → localStorage mirror → default. The ref
  // mirror is read inside the once-registered onNewMessage listener.
  const [mentionVolume, setMentionVolume] = useState(() => {
    try {
      const saved = localStorage.getItem(MENTION_VOLUME_STORAGE_KEY)
      return saved != null ? clampVolume(saved) : DEFAULT_MENTION_VOLUME
    } catch {
      return DEFAULT_MENTION_VOLUME
    }
  })
  const mentionVolumeRef = useRef(mentionVolume)
  mentionVolumeRef.current = mentionVolume
  const mentionVolumePickedThisSession = useRef(false)
  const handleMentionVolumeChange = useCallback((value) => {
    const vol = clampVolume(value)
    mentionVolumePickedThisSession.current = true
    setMentionVolume(vol)
    try {
      localStorage.setItem(MENTION_VOLUME_STORAGE_KEY, String(vol))
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
    socketService.setMentionVolume(vol)
  }, [])
  // Preview the sound at the chosen volume — fired by the dialog when the user
  // releases the volume slider (not on every drag tick).
  const handleMentionVolumePreview = useCallback(() => {
    playMentionSound(mentionSoundRef.current, mentionVolumeRef.current)
  }, [])

  // Chat message FONT SIZE ('small' | 'medium' | 'large'). Passed to ChatPanel.
  const [fontSize, setFontSize] = useState(() => {
    try {
      const saved = localStorage.getItem(FONT_SIZE_STORAGE_KEY)
      return FONT_SIZES.includes(saved) ? saved : DEFAULT_FONT_SIZE
    } catch {
      return DEFAULT_FONT_SIZE
    }
  })
  const fontSizePickedThisSession = useRef(false)
  const handleFontSizeChange = useCallback((value) => {
    if (!FONT_SIZES.includes(value)) return
    fontSizePickedThisSession.current = true
    setFontSize(value)
    try {
      localStorage.setItem(FONT_SIZE_STORAGE_KEY, value)
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
    socketService.setFontSize(value)
  }, [])

  // Message TIMESTAMP FORMAT ('relative' | 'clock' | 'off'). Passed to ChatPanel.
  const [timestampFormat, setTimestampFormat] = useState(() => {
    try {
      const saved = localStorage.getItem(TIMESTAMP_FORMAT_STORAGE_KEY)
      return TIMESTAMP_FORMATS.includes(saved) ? saved : DEFAULT_TIMESTAMP_FORMAT
    } catch {
      return DEFAULT_TIMESTAMP_FORMAT
    }
  })
  const timestampFormatPickedThisSession = useRef(false)
  const handleTimestampFormatChange = useCallback((value) => {
    if (!TIMESTAMP_FORMATS.includes(value)) return
    timestampFormatPickedThisSession.current = true
    setTimestampFormat(value)
    try {
      localStorage.setItem(TIMESTAMP_FORMAT_STORAGE_KEY, value)
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
    socketService.setTimestampFormat(value)
  }, [])

  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  // Full auth payload (user + token + tenant) kept in memory for the socket
  // connect. It is ALSO persisted in localStorage (not consumed/deleted on
  // load), so a reload re-hydrates it and the axios interceptor can read the
  // token for REST calls.
  const [authData, setAuthData] = useState(null)
  const [tenantInfo, setTenantInfo] = useState(null)
  const [tenantConfig, setTenantConfig] = useState(null)
  // The user whose read-only ProfileCard is open (from a user-row "Profile"
  // action), or null when closed.
  const [profileCardUser, setProfileCardUser] = useState(null)
  // The single shared user context menu (summoned from sidebar rows AND chat
  // usernames): { userId, position: {top,left} } | null. Only the id + anchor
  // are stored — the enriched user object is re-derived every render so the
  // open menu tracks live state (webcam on/off, presence) instead of a
  // snapshot from summon time.
  const [userMenu, setUserMenu] = useState(null)
  // Friends v1 — my friend graph in this workspace, as three Sets of user ids.
  // Loaded once post-join-ack (friend:list), then kept live by the two friend
  // subscription events and every successful friend action ack (optimistic,
  // reverted on rejection). friendIds are mutual ACCEPTED partners; incoming =
  // PENDING requests awaiting MY response; outgoing = PENDING I sent.
  const [friendIds, setFriendIds] = useState(() => new Set())
  const [incomingRequestIds, setIncomingRequestIds] = useState(() => new Set())
  const [outgoingRequestIds, setOutgoingRequestIds] = useState(() => new Set())
  // Ignore / Mute v1 — user-ids I've muted in this workspace (private, client-
  // enforced). Seeded from the REST auth membership on load, kept live by the
  // ignore:set ack. Hides the ignored user's chat messages (render-time filter,
  // never deleted from state so unignore resurrects history), IM windows, and
  // mention pings. Never notifies them, never broadcasts.
  const [ignoredUserIds, setIgnoredUserIds] = useState(() => new Set())
  // Live mirror of ignoredUserIds for the once-registered listeners (onNewMessage
  // ding, ingestPrivateMessage) that would otherwise close over a stale Set.
  const ignoredUserIdsRef = useRef(ignoredUserIds)
  ignoredUserIdsRef.current = ignoredUserIds
  // Quick Replies — my saved composer snippets [{ id, text, uses, createdAt }].
  // Seeded from the REST auth membership, rehydrated by the join-chat ack, and
  // kept live by the quickreply:set ack (full-list replace; server owns the
  // ids/uses/createdAt fields). Order is user-controlled.
  // The server stores sanitizeContent output (entity-ENCODED, like message
  // wire content), so every list that enters state is decoded here once —
  // display/compare/insert all work in plain text, and the server re-encodes
  // on the next save. Same layering as messages (store-encoded/render-decoded).
  const [quickReplies, setQuickReplies] = useState([])
  const [messages, setMessages] = useState([])
  const [onlineUsers, setOnlineUsers] = useState([])
  const [watchingUsers, setWatchingUsers] = useState([]) // Users whose webcams we're watching
  // Typing-presence debounce state. `active` guards the leading-edge
  // typing-start emit (so a burst of keystrokes emits at most once), and the
  // trailing timer fires typing-stop only after the user pauses — see
  // handleTyping. A ref (not state) because it must not trigger re-renders.
  const typingRef = useRef({ active: false, stopTimer: null })
  // WebRTC video state.
  // producersByUser: userId -> producerId for everyone currently publishing a
  // webcam in the room (seeded from rtc:get-rtp-capabilities, kept live by
  // rtc:new-producer / rtc:producer-closed). Drives the "has video" indicator.
  const [producersByUser, setProducersByUser] = useState({})
  // Mirror of producersByUser for use inside once-registered socket listeners
  // (the rtc:webcam-accept handler must read the CURRENT producer map, not the
  // snapshot captured when listeners were registered).
  const producersByUserRef = useRef({})
  // audioProducersByUser: userId -> audio producerId for everyone publishing a
  // mic. Parallel to producersByUser (which is video-only). The consume path
  // reads this to bundle a user's audio into the same tile stream as their
  // video. Mirrored in a ref for once-registered listeners (onWebcamAccept).
  const audioProducersByUserRef = useRef({})
  // In-flight watch requests (userIds) — synchronous guard so a double-click
  // can't fire two webcam requests before watchingUsers state updates.
  const pendingWatchRef = useRef(new Set())
  // Watch requests of mine a PRIVATE publisher queued for manual approval
  // (userIds), learned from their rtc:webcam-queued ack. The publisher's
  // private/public mode never leaves their client, so this ack is the only
  // truthful signal that a request is actually gated — the "You requested…"
  // notice prints on it (never at click time), and a later accept always shows
  // "You are now watching…" for these (a real approval), while auto-accepted
  // public watches gate that notice behind the tenant's watchAck setting.
  const queuedWatchTargetsRef = useRef(new Set())
  // Mirror of tenantConfig.features.watchAck for once-registered socket
  // listeners (the captured tenantConfig would be stale). Default ON.
  const watchAckEnabledRef = useRef(true)
  // Mirror of tenantConfig.features.friends for the post-join friend:list call
  // inside the memoized connectToChat (same stale-closure reason). Default ON.
  const friendsEnabledRef = useRef(true)
  // consumedStreams: userId -> MediaStream we've attached for users we watch.
  const [consumedStreams, setConsumedStreams] = useState({})
  // pausedVideoUsers: userIds whose VIDEO producer is currently paused (they
  // toggled their cam off mid-publish). Their tile shows a "paused" placeholder
  // instead of a frozen last frame. Audio pauses just go silent — no UI needed
  // for a watcher — so this tracks video only. Driven by rtc:producer-paused.
  const [pausedVideoUsers, setPausedVideoUsers] = useState([])
  // watchedByUsers: userIds currently watching MY cam (drives the "they watch
  // me" eye next to each user). Kept live by rtc:watcher-added/removed.
  const [watchedByUsers, setWatchedByUsers] = useState([])
  // blockedWatchers: userIds I (as publisher) have blocked from watching MY cam
  // via the eye toggle. The eye on these rows renders slashed; a block also
  // kicks any active session server-side. Session-scoped — cleared when I stop
  // publishing / log out (the server drops the block on my disconnect too).
  const [blockedWatchers, setBlockedWatchers] = useState([])
  // blockedByUsers: userIds of publishers who have blocked ME from their cam.
  // While present, their row hides its watch button (I can't (re)start a watch).
  // Driven by rtc:cam-blocked / rtc:cam-unblocked.
  const [blockedByUsers, setBlockedByUsers] = useState([])
  // Our own SFU-consumed output stream (the self-monitor). Consuming our own
  // producer lets us watch what others actually receive (post-encode), not the
  // raw camera. Drives the inline sidebar monitor AND the grid "You" tile.
  const [selfStream, setSelfStream] = useState(null)
  // Our own publishing state + chosen output quality (low|default|high).
  // Defaults to 'high' for now; this will become a chat-admin-configurable
  // setting (per-tenant default the user can still override in ProfileDialog).
  const [isPublishing, setIsPublishing] = useState(false)
  const [outputQuality, setOutputQuality] = useState("high")
  // Per-track publish toggles (the sidebar cam/mic icons). These control WHAT is
  // in our publish while it's live — they pause/resume the video/audio producers
  // rather than starting/stopping the whole publish (that's Enable/Disable
  // Webcam). Both reset to true whenever we (re)enable the webcam.
  const [videoTrackOn, setVideoTrackOn] = useState(true)
  const [micEnabled, setMicEnabled] = useState(true)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionError, setConnectionError] = useState(null)
  const [hasConnectedAfterAuth, setHasConnectedAfterAuth] = useState(false)
  // AFK idle policy. `idleDeadline` is an epoch-ms timestamp computed from the
  // grace DURATION the server sends (never a server absolute — clock skew), or
  // null when no warning is showing. `idleKicked` swaps the chat for the Rejoin
  // screen while keeping the session authenticated.
  const [idleDeadline, setIdleDeadline] = useState(null)
  const [idleConfirming, setIdleConfirming] = useState(false)
  const [idleKicked, setIdleKicked] = useState(false)

  // Browser-level presence signal (mousemove/keydown/scroll/tab focus). Only
  // runs when the tenant enabled the idle policy — with the policy off there is
  // nothing to feed, and the presence dot keeps its old server-action-only
  // behavior. Read straight from state, not a ref: the hook is called during
  // render, so there is no stale-closure problem to solve here (unlike the
  // flags consumed inside once-registered socket listeners).
  const { pingNow: idlePingNow } = useActivityPing(
    isAuthenticated && !idleKicked && !!tenantConfig?.features?.idleKick?.enabled,
    !!idleDeadline
  )
  // Count of outbound messages buffered in socketService while disconnected.
  // Mirrors socketService.pendingCount; drives the "N will send when
  // reconnected" hint in the chat composer so queued text never looks lost.
  const [pendingMessageCount, setPendingMessageCount] = useState(0)
  // Auto-reconnect state machine (drives the reconnection modal). null = not
  // reconnecting. Otherwise { attempt, phase } where phase is:
  //   'waiting'    — counting down to the next attempt
  //   'connecting' — an attempt is in flight
  //   'failed'     — all RECONNECT_DELAYS attempts exhausted (manual Retry/Leave)
  const [reconnect, setReconnect] = useState(null)
  // True while the current reconnect is a KNOWN server restart (deploy) rather
  // than a network failure — drives calmer "updating, back in a moment" copy in
  // the reconnect modal. Mirrors maintenanceRef (which the reconnect machine
  // reads synchronously).
  const [isMaintenance, setIsMaintenance] = useState(false)
  // Set to the kicking admin's username when an admin removes us from the room.
  // Drives the terminal "You have been kicked by X" modal; the socket is already
  // being closed server-side, so this is a notice, not a reconnect prompt.
  const [kickedBy, setKickedBy] = useState(null)
  // Timer handle for the pending backoff attempt, so we can cancel on
  // success/leave/unmount.
  const reconnectTimerRef = useRef(null)
  // Guard so overlapping triggers (a disconnect firing while an attempt is
  // already scheduled) don't spin up parallel backoff loops.
  const reconnectingRef = useRef(false)
  // Set when a 'server-restarting' event preceded the drop, so the reconnect
  // machine waits for the server to return before the first attempt.
  const maintenanceRef = useRef(false)
  // The webcam-publish "intent" — the opts we last published with, or null when
  // not publishing. A reconnect wipes the server's SFU state (producers live in
  // the backend process), so after re-joining we re-publish with this so the
  // user's camera comes back automatically instead of staying dead until they
  // manually re-toggle. Set on successful publish, cleared on intentional stop.
  const publishIntentRef = useRef(null)
  // Always-current handle to handleEnableWebcam so the memoized connectToChat
  // can auto-republish via the LATEST closure (not a stale one captured when
  // connectToChat was last memoized). Assigned just below the function def.
  const enableWebcamRef = useRef(null)
  // Live mic-on state mirrored into a ref so auto-republish (in the memoized
  // connectToChat) re-publishes with the user's CURRENT mute choice, not the
  // value captured when they first published. Without this, muting mid-session
  // then surviving a deploy would bring the user back UNMUTED. Kept in sync each
  // render from micEnabled.
  const liveMicOnRef = useRef(true)
  // The user's chosen chat text color. Whole future messages render in this
  // color (server tints each message via membership.textColor). Hydrated from
  // the server (members) or localStorage (guests); see hydrateColor below.
  const [currentColor, setCurrentColor] = useState(null)
  // The user's fixed USERNAME color — independent of the message text color
  // above. Hydrated from the server (membership.usernameColor, resolved to a
  // role default when unset). Members pick their own via the profile dialog;
  // guests are not pickable and just take the default.
  const [usernameColor, setUsernameColor] = useState(null)
  // The user's profile fields (per-tenant): a short bio and a solid banner
  // background color for their profile hero. Hydrated from the membership on auth;
  // members edit them in the profile dialog. '' = unset (banner → theme default).
  const [profileBio, setProfileBio] = useState('')
  const [profileBannerColor, setProfileBannerColor] = useState('')
  // The user's chosen chat display mode: 'default' | 'compact' | 'bubble'.
  // Hydrated/persisted the same way as currentColor.
  const [currentViewMode, setCurrentViewMode] = useState("default")
  // Popped-out users (userIds) — tracks which users are popped out to separate windows. The VideoGrid renders popped-out users in the separate windows instead of the main grid.
  const [poppedOutUsers, setPoppedOutUsers] = useState([]);
  // User's manual tile arrangement (array of userIds). Empty = use the default
  // open-order (see getWatchedParticipants). Once the user drags a tile, this
  // holds the explicit order they set; it wins over open-order. Session-only
  // (in-memory) — resets on reload, like the other tile UI state (pin/reveal).
  // Newly-opened cams not yet in this list sort to the END in open order.
  const [tileOrder, setTileOrder] = useState([]);
  // "Stats for nerds" open state is OWNED HERE (keyed by userId), not inside the
  // tile, so it survives the tile unmount/remount when a user is popped out/in
  // (the docked grid tile and the popped-out windowed tile are different React
  // instances of VideoParticipantTile). statsAnchors remembers where to first
  // place each panel.
  const [statsOpenUsers, setStatsOpenUsers] = useState([]);
  const statsAnchorsRef = useRef({});
  // Last dragged position of each user's stats panel (keyed by userId), lifted
  // here so it survives the tile's unmount/remount on pop-out/in — otherwise the
  // fresh panel instance snaps back to its anchor.
  const statsPosRef = useRef({});

  const handleToggleStats = (userId, rect) => {
    setStatsOpenUsers((prev) => {
      if (prev.includes(userId)) return prev.filter((id) => id !== userId);
      if (rect) statsAnchorsRef.current[userId] = rect;
      return [...prev, userId];
    });
  };
  const handleCloseStats = (userId) =>
    setStatsOpenUsers((prev) => prev.filter((id) => id !== userId));
  const handleStatsPositionChange = (userId, pos) => {
    statsPosRef.current[userId] = pos;
  };
  // Webcam mode: 'public' (default) or 'private' (only show webcam to users in the same room). This is a local UI state for now, but could be persisted to the server as a membership property if needed.
  const [webcamMode, setWebcamMode] = useState('public') // 'public' or 'private'
  // Mirror of webcamMode for the once-registered onWebcamRequest listener, which
  // would otherwise capture the initial 'public' value forever (stale closure).
  const webcamModeRef = useRef('public')
  // Incoming cam requests awaiting my approval (private mode). Each entry:
  // { fromUserId, fromUsername }. Rendered as a centered approval banner.
  const [pendingCamRequests, setPendingCamRequests] = useState([])
  // Incoming cam INVITES awaiting my decision — a private publisher invited me
  // to view THEIR cam. Each entry { fromUserId, fromUsername }. Rendered in the
  // same banner as requests, but with invite copy + an Accept/Deny/Ignore row.
  const [pendingCamInvites, setPendingCamInvites] = useState([])
  // Anti-spam cooldown: publishers (userIds) who recently DENIED my watch
  // request. Their cam icon is dimmed + unclickable until the cooldown expires.
  const [deniedCooldownUsers, setDeniedCooldownUsers] = useState([])
  const CAM_REQUEST_COOLDOWN_MS = 12000
  // One-shot pre-acceptance tokens: userIds I (a private publisher) have invited
  // to view my cam and who haven't yet accepted. When one of them sends a
  // viaInvite webcam-request, I auto-accept it ONCE and drop the token. A plain
  // cam-icon click (no token / viaInvite:false) is never auto-accepted — it goes
  // through the normal private-mode approval banner. A ref (not state) so the
  // once-registered onWebcamRequest listener reads the live set.
  const outstandingInvitesRef = useRef(new Set())

  // --- Peer-to-peer file transfer -----------------------------------------
  // Incoming file OFFERS awaiting my decision. Each entry:
  // { fromUserId, fromUsername, transferId, name, size, mime }. Rendered in the
  // same banner style as cam requests, with Accept/Deny.
  const [pendingFileOffers, setPendingFileOffers] = useState([])
  // Active transfers (send OR receive) for the progress UI. Keyed by transferId
  // -> { dir:'send'|'recv', peerUsername, name, size, sent }.
  const [fileTransfers, setFileTransfers] = useState({})
  // OUTGOING offers I've sent that haven't been accepted yet: transferId -> the
  // File object, held until the recipient accepts (then streamed) or
  // denies/cancels (then dropped). A ref — the file:accept listener reads the
  // live map without a stale closure, and File blobs shouldn't drive renders.
  const outgoingFilesRef = useRef(new Map())
  // INCOMING reassembly buffers: transferId -> { chunks:[], received, meta }.
  // A ref for the same reasons — large binary buffers must not re-render chat.
  const incomingFilesRef = useRef(new Map())
  // Hidden <input type=file> used to pick a file to send, plus the target user
  // chosen from the row menu (set just before the picker opens).
  const fileSendInputRef = useRef(null)
  const fileSendTargetRef = useRef(null)

  // --- IM mode (AIM-style private chat) -----------------------------------
  // Togglable client preference for the OUTGOING affordance: when ON, you can
  // open IM windows yourself (row-menu "Private chat") and hear the AIM door/
  // ding sounds. INCOMING private messages ALWAYS pop a window regardless — a
  // buddy IMing you opening a window is the whole point of an instant messenger.
  // Defaults ON; persisted to localStorage (a pure client pref, like theme/font).
  const [imMode, setImMode] = useState(() => {
    try {
      return localStorage.getItem(IM_MODE_STORAGE_KEY) !== "off"
    } catch {
      return true
    }
  })
  // Retro AIM-style skin for IM windows (overrides the app theme). Default OFF.
  const [imClassic, setImClassic] = useState(() => {
    try {
      return localStorage.getItem(IM_CLASSIC_STORAGE_KEY) === "on"
    } catch {
      return false
    }
  })
  // AIM message sounds (Send/Recv MP3 samples). Default ON. Toggled from the
  // classic IM window's Edit menu.
  const [imSounds, setImSounds] = useState(() => {
    try {
      return localStorage.getItem(IM_SOUNDS_STORAGE_KEY) !== "off"
    } catch {
      return true
    }
  })
  // The IM window manager API (open/close/focus/update windows). The windows
  // themselves (geometry, z-order, minimize/maximize, taskbar) are owned by the
  // manager; App owns only the CONVERSATION data + the window↔conversation
  // binding below.
  const wm = useWindowManager()
  // Conversations keyed by partner userId:
  //   { partner:{id,username,usernameColor,role}, messages:[{id,mine,content,
  //     type,away,timestamp}], typing:bool, unread:bool }
  const [imConversations, setImConversations] = useState({})
  // Live ref to the conversations so once-registered socket listeners and the
  // window render factory read the latest without stale closures.
  const imConversationsRef = useRef(imConversations)
  imConversationsRef.current = imConversations
  // partnerId -> window id (`im:<partnerId>`). Lets us dedupe/focus/update an
  // existing IM window and tear them all down on logout.
  const imWindowIdsRef = useRef({})
  // Per-partner "partner is typing" auto-clear timers.
  const imTypingTimersRef = useRef({})
  // My AIM-style away message (session-scoped; relayed to the server which
  // auto-replies on my behalf). '' = not away.
  const [awayMessage, setAwayMessage] = useState("")

  // Tear down all IM state — conversations, open windows, typing timers, away.
  // Called on logout / displacement / leave so a fresh session doesn't inherit
  // stale private chats (which are session-scoped anyway). Defined up here (with
  // the IM state) so the reset sites below can reference it without a
  // use-before-define.
  const resetImState = useCallback(() => {
    Object.values(imTypingTimersRef.current).forEach(clearTimeout)
    imTypingTimersRef.current = {}
    Object.values(imWindowIdsRef.current).forEach((wid) => wm.closeWindow(wid))
    imWindowIdsRef.current = {}
    setImConversations({})
    setAwayMessage("")
  }, [wm])

  // Pre-join device/stream setup modal. Shown on entry so the user can preview
  // and pick their camera/mic before going live. Guests always see it; members
  // see it until they check "don't show again" (persisted per-tenant). Chosen
  // settings are reused on future entry. `deviceSetup` holds the loaded saved
  // settings (or null). `setupDecidedRef` guards the once-per-entry trigger.
  const [showSetupModal, setShowSetupModal] = useState(false)
  // True when the modal was reopened mid-session via the gear (vs the pre-join
  // prompt) — drives the dismiss button label (Cancel vs Skip).
  const [setupReopened, setSetupReopened] = useState(false)
  const [deviceSetup, setDeviceSetup] = useState(null)
  const setupDecidedRef = useRef(false)

  // --- Group "Sesh" Countdown (the signature feature) ---
  // The CURRENT sesh state, server-authoritative. null = no active sesh.
  // Otherwise the latest snapshot the server broadcast:
  //   { phase: 'join'|'prep'|'done', secondsLeft, total, smokers, initiator }.
  // Updated by the sesh:* listeners registered in setupSocketListeners. Every
  // field comes from the server (we never run our own countdown) — including a
  // correct mid-countdown snapshot on late join (sesh:state). The SeshBanner
  // renders this; the start/end SYSTEM chat notices come from the server too,
  // so we don't double-post them here.
  const [sesh, setSesh] = useState(null)
  // True once the join ack has told us whether a sesh is active. Until then we
  // don't know, so TopBar hides the start button to avoid a button→status flash
  // on a reload mid-sesh. Reset on disconnect so a reconnect re-gates.
  const [seshSynced, setSeshSynced] = useState(false)
  // "Noodle shame" — the silly gag. When a member starts a sesh that lands as a
  // NOODLE (near-miss of the :20/:50), the initiator wears a jester hat on their
  // avatar until the 420/350 they fumbled actually arrives. Server-authoritative:
  // { userId, username, shameUntil (ms epoch) } from the sesh:noodle-shame event
  // (and re-sent on late join). null = nobody's currently shamed. A timer clears
  // it at shameUntil so the hat comes off exactly when the boundary hits.
  const [noodleShame, setNoodleShame] = useState(null)
  // Holds the pending clear-at-shameUntil timeout so a fresh noodle can cancel a
  // stale one (a new noodler replaces the old).
  const noodleShameTimerRef = useRef(null)
  // Live head-tracking transform for the current noodler, when they're on camera
  // and streaming it: { cx, cy, wFrac, rollDeg } (fractions of the video frame +
  // degrees) or null. Watchers apply this to the noodler's video tile so the hat
  // follows their head; null = show the static hat (their cam is off, they're on
  // a device that can't track, or their face is momentarily lost). Set from the
  // sesh:noodle-track relay; cleared when the shame ends.
  const [noodleTrack, setNoodleTrack] = useState(null)
  // Stop() for our own head tracker while WE are the noodler (null when not
  // tracking). Lets us tear it down when the shame ends or the camera goes off.
  const headTrackStopRef = useRef(null)
  // Guards the one-shot auto-start of the webcam on a suppressed (don't-show-
  // again) entry, so it fires at most once per session.
  const autoEnabledRef = useRef(false)

  // Extract tenant information and check for existing auth on app load
  useEffect(() => {
    // Get tenant info from URL
    const info = getTenantInfoFromURL()
    if (!info) {
      console.error("No tenant information found in URL")
      return
    }
    setTenantInfo(info)

    // Load tenant configuration
    const loadTenantConfig = async () => {
      try {
        const config = await authAPI.getConfig(info.tenantId, info.scriptHash)
        setTenantConfig(config)
        // Mirror the watchAck flag for once-registered socket listeners
        // (onWebcamAccept). Missing/absent = ON.
        watchAckEnabledRef.current = config?.features?.watchAck !== false
        // Tenant policy: the vocal chain's -1 dBFS voice limiter. Default ON —
        // only an explicit false (tenant admin) disables. Applies live.
        voiceChain.setLimiterEnabled(config?.features?.voiceLimiter !== false)
        // Default ON: only an explicit false (set by the tenant admin) disables.
        friendsEnabledRef.current = config?.features?.friends !== false
        if (config?.tenant?.name) {
          document.title = config.tenant.name
        }
      } catch (error) {
        console.error("Failed to load tenant config:", error)
      }
    }
    loadTenantConfig()

    // Load existing auth WITHOUT consuming it. The token must stay in
    // localStorage so it (a) survives a page reload and (b) remains readable by
    // the axios request interceptor for REST calls. Multi-tab safety is handled
    // authoritatively by the server's connection-displacement event
    // (onDisplaced) — a newer tab kicks older ones — not by deleting storage on
    // load, which used to strand the user (reload → no token → bounced to login).
    const savedAuth = authStorage.load()
    if (savedAuth && !authStorage.isExpired(savedAuth)) {
      // Verify the saved auth matches current tenant
      if (savedAuth.tenant?.id === info.tenantId) {
        console.log("Loaded valid saved auth:", savedAuth.user)
        setUser(savedAuth.user)
        setAuthData(savedAuth)
        setIsAuthenticated(true)
      } else {
        // Auth is for a different tenant
        console.log("Saved auth is for different tenant, ignoring...")
      }
    } else if (savedAuth) {
      console.log("Saved auth expired, clearing...")
      authStorage.clear()
    }
  }, [])

  const handleLogin = async (formData) => {
    if (!tenantInfo) {
      throw new Error("Tenant information not available")
    }

    try {
      let authData

      if (formData.isRegistration) {
        // Registration flow
        console.log("Registration attempt:", formData)
        authData = await authAPI.register(
          tenantInfo.tenantId,
          tenantInfo.scriptHash,
          formData
        )
      } else {
        // Login flow
        console.log("Login attempt:", formData)
        authData = await authAPI.login(
          tenantInfo.tenantId,
          tenantInfo.scriptHash,
          formData
        )
      }

      // Save auth state with tenant information
      const completeAuthData = {
        user: authData.user,
        token: authData.token,
        tenant: authData.tenant,
        membership: authData.membership,
        tenantId: tenantInfo.tenantId,
        scriptHash: tenantInfo.scriptHash,
      }

      authStorage.save(completeAuthData)
      // Set auth state only. The auto-connect effect below is the SINGLE place
      // that calls connectToChat — calling it here too would race the effect and
      // open two sockets (duplicate "Welcome ..." message + userlist dropouts).
      setUser(authData.user)
      setAuthData(completeAuthData)
      setIsAuthenticated(true)
      // A fresh login supersedes any lingering failure notice (e.g. the
      // displacement message) — don't let it haunt the next screen.
      setConnectionError(null)
    } catch (error) {
      if (error instanceof APIError) {
        throw new Error(error.message)
      }
      throw new Error("Authentication failed. Please try again.")
    }
  }

  const handleGuestLogin = async () => {
    if (!tenantInfo) {
      throw new Error("Tenant information not available")
    }

    try {
      console.log("Guest login attempt")
      const authData = await authAPI.guest(
        tenantInfo.tenantId,
        tenantInfo.scriptHash
      )

      // Save guest auth state (shorter expiry)
      const completeAuthData = {
        user: authData.user,
        token: authData.token,
        tenant: authData.tenant,
        membership: authData.membership,
        tenantId: tenantInfo.tenantId,
        scriptHash: tenantInfo.scriptHash,
        isGuest: true,
      }

      console.log("Saving guest auth data:", completeAuthData)
      authStorage.save(completeAuthData)
      // Set auth state only — the auto-connect effect handles the single
      // socket connect (see handleLogin for why we don't connect here).
      setUser(authData.user)
      setAuthData(completeAuthData)
      setIsAuthenticated(true)
      // A fresh login supersedes any lingering failure notice.
      setConnectionError(null)
    } catch (error) {
      console.error("Guest login failed:", error)
      // Surface the reason on the auth screen (e.g. the tenant turned guest
      // access off). There used to be a tokenless "local guest" fallback here,
      // but it could never connect and would have let a refused guest past
      // the login screen into a dead session.
      if (error instanceof APIError) {
        throw new Error(error.message)
      }
      throw new Error("Guest login failed. Please try again.")
    }
  }

  // Tenant-scoped localStorage key for the chosen color. Guests have no durable
  // server-side color (their membership is disposable), so the browser is their
  // store; members use it as an offline mirror of the server value.
  const colorStorageKey = useCallback(
    () => (tenantInfo ? `chatColor_${tenantInfo.tenantId}` : null),
    [tenantInfo]
  )

  // Hydrate the chosen color whenever auth changes. Guests prefer the browser
  // value (server only ever returns their role default); members prefer the
  // server value and fall back to the browser mirror.
  useEffect(() => {
    if (!authData) return
    const key = colorStorageKey()
    const stored = key ? localStorage.getItem(key) : null
    const serverColor = authData.membership?.textColor
    const isGuest = authData.isGuest
    const resolved = isGuest
      ? stored || serverColor || null
      : serverColor || stored || null
    if (resolved) setCurrentColor(resolved)
  }, [authData, colorStorageKey])

  // Hydrate the username color from the server on auth change. The server
  // always resolves this to a concrete color (chosen, else role default), so
  // it's authoritative for everyone — no localStorage mirror needed (guests
  // can't pick one anyway).
  useEffect(() => {
    if (!authData) return
    const serverUsernameColor = authData.membership?.usernameColor
    if (serverUsernameColor) setUsernameColor(serverUsernameColor)
  }, [authData])

  // Hydrate the profile bio + banner color from the membership on auth. Both are
  // authoritative from the server (per-tenant); no localStorage mirror needed.
  useEffect(() => {
    if (!authData) return
    setProfileBio(authData.membership?.bio || '')
    setProfileBannerColor(authData.membership?.bannerColor || '')
  }, [authData])

  // Seed the ignore list from the auth membership when it's present (a fresh
  // login response carries it). We DON'T reset to empty when the key is absent:
  // a reload restores authData from a possibly-stale localStorage copy that
  // predates this field, and the authoritative rehydration is the join-chat ack
  // (see connectToChat). The ignore:set ack keeps it live in-session.
  useEffect(() => {
    if (!authData) return
    const saved = authData.membership?.ignoredUserIds
    if (Array.isArray(saved)) setIgnoredUserIds(new Set(saved))
  }, [authData])

  // Seed quick replies from the auth membership the same way (and with the same
  // stale-localStorage caveat) as the ignore list above; the join-chat ack is
  // the authoritative rehydration.
  useEffect(() => {
    if (!authData) return
    const saved = authData.membership?.quickReplies
    if (Array.isArray(saved)) setQuickReplies(decodeQuickReplies(saved))
  }, [authData])

  // Persist a full-list replace of my quick replies. State re-syncs from the
  // server ack (the server assigns ids and preserves uses/createdAt); the
  // rejection propagates so the calling UI can show the server's error.
  const handleQuickRepliesChange = useCallback(async (nextList) => {
    const ack = await socketService.setQuickReplies(nextList)
    setQuickReplies(decodeQuickReplies(ack.quickReplies || []))
    return ack
  }, [])

  // Hydrate the active theme from the server/tenant once auth + config arrive.
  // Priority: the user's own membership override → the tenant default → the
  // localStorage mirror (already applied as the initial state) → DEFAULT_THEME.
  // We DON'T override a choice the user made live this session (the ref guard),
  // so flipping themes and then a late config load won't yank it back.
  useEffect(() => {
    if (themePickedThisSession.current) return
    const userTheme = authData?.membership?.theme // per-user override (members)
    const tenantDefault = tenantConfig?.ui?.defaultTheme // house default
    const resolved = userTheme || tenantDefault
    if (resolved && getTheme(resolved) && resolved !== themeName) {
      setThemeName(resolved)
      try {
        localStorage.setItem(THEME_STORAGE_KEY, resolved)
      } catch {
        /* ignore */
      }
    }
    // themeName intentionally omitted: this should react to incoming
    // server/tenant values, not re-run when it sets themeName itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData, tenantConfig])

  // Hydrate the mention-sound preference from the membership once auth arrives.
  // The user's saved override wins over the localStorage mirror (already the
  // initial state). Guests have no membership value, so they keep the default.
  useEffect(() => {
    if (mentionSoundPickedThisSession.current) return
    const saved = authData?.membership?.mentionSound
    if (MENTION_SOUNDS.includes(saved) && saved !== mentionSound) {
      setMentionSound(saved)
      try {
        localStorage.setItem(MENTION_SOUND_STORAGE_KEY, saved)
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData])

  // Hydrate the mention-VOLUME from the membership once auth arrives. Same
  // precedence as the sound: saved override → localStorage mirror → default.
  useEffect(() => {
    if (mentionVolumePickedThisSession.current) return
    const saved = authData?.membership?.mentionVolume
    if (saved != null) {
      const vol = clampVolume(saved)
      if (vol !== mentionVolume) {
        setMentionVolume(vol)
        try {
          localStorage.setItem(MENTION_VOLUME_STORAGE_KEY, String(vol))
        } catch {
          /* ignore */
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData])

  // Hydrate the chat FONT SIZE from the membership once auth arrives.
  useEffect(() => {
    if (fontSizePickedThisSession.current) return
    const saved = authData?.membership?.fontSize
    if (FONT_SIZES.includes(saved) && saved !== fontSize) {
      setFontSize(saved)
      try {
        localStorage.setItem(FONT_SIZE_STORAGE_KEY, saved)
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData])

  // Hydrate the TIMESTAMP FORMAT from the membership once auth arrives.
  useEffect(() => {
    if (timestampFormatPickedThisSession.current) return
    const saved = authData?.membership?.timestampFormat
    if (TIMESTAMP_FORMATS.includes(saved) && saved !== timestampFormat) {
      setTimestampFormat(saved)
      try {
        localStorage.setItem(TIMESTAMP_FORMAT_STORAGE_KEY, saved)
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData])

  // Set the active default color: persist to the membership (socket) and mirror
  // to localStorage so it survives reloads (guests) / offline (members).
  const handleColorChange = useCallback(
    (color) => {
      if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color || "")) return
      setCurrentColor(color)
      const key = colorStorageKey()
      if (key) localStorage.setItem(key, color)
      socketService.setColor(color)
    },
    [colorStorageKey]
  )

  // Set the username color: persist to the membership via socket and optimistically
  // update local state. The server enforces the admin-reserved band and rejects
  // colors a non-staff member isn't allowed to use.
  const handleUsernameColorChange = useCallback((color) => {
    if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color || "")) return
    setUsernameColor(color)
    socketService.setUsernameColor(color)
  }, [])

  // Profile bio: optimistic local update + persist via socket. The server strips
  // control chars and caps at 200 chars; '' clears it.
  const handleBioChange = useCallback((bio) => {
    const next = (bio || '').slice(0, 200)
    setProfileBio(next)
    socketService.setBio(next)
  }, [])

  // Profile banner color: optimistic local update + persist. '' clears it (→ theme
  // default). The server re-validates the '#rrggbb' shape.
  const handleBannerColorChange = useCallback((color) => {
    const next = color || ''
    if (next && !/^#[0-9a-fA-F]{6}$/.test(next)) return
    setProfileBannerColor(next)
    socketService.setBannerColor(next)
  }, [])

  // Tenant-scoped localStorage key for the chosen display mode.
  const viewModeStorageKey = useCallback(
    () => (tenantInfo ? `chatViewMode_${tenantInfo.tenantId}` : null),
    [tenantInfo]
  )

  // Hydrate display mode on auth change. Guests prefer the browser value;
  // members prefer the server value (membership.viewMode) and fall back to it.
  useEffect(() => {
    if (!authData) return
    const key = viewModeStorageKey()
    const stored = key ? localStorage.getItem(key) : null
    const serverMode = authData.membership?.viewMode
    const isGuest = authData.isGuest
    const resolved = isGuest
      ? stored || serverMode
      : serverMode || stored
    if (resolved) setCurrentViewMode(resolved)
  }, [authData, viewModeStorageKey])

  // Set the active display mode: persist to membership (socket) + localStorage.
  const handleViewModeChange = useCallback(
    (mode) => {
      if (!["default", "compact", "bubble"].includes(mode)) return
      setCurrentViewMode(mode)
      const key = viewModeStorageKey()
      if (key) localStorage.setItem(key, mode)
      socketService.setViewMode(mode)
    },
    [viewModeStorageKey]
  )

  // Tenant-scoped localStorage key for the saved device-setup settings
  // ({ videoDeviceId, audioDeviceId, videoOn, micOn, dontShowAgain }).
  const deviceSetupStorageKey = useCallback(
    () => (tenantInfo ? `chatDeviceSetup_${tenantInfo.tenantId}` : null),
    [tenantInfo]
  )

  const loadDeviceSetup = useCallback(() => {
    const key = deviceSetupStorageKey()
    if (!key) return null
    try {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }, [deviceSetupStorageKey])

  const saveDeviceSetup = useCallback(
    (settings) => {
      const key = deviceSetupStorageKey()
      if (!key) return
      try {
        localStorage.setItem(key, JSON.stringify(settings))
      } catch {
        /* ignore (private mode / quota) */
      }
      setDeviceSetup(settings)
    },
    [deviceSetupStorageKey]
  )

  // --- Microphone processing (vocal chain) prefs ---
  // Tenant-scoped persisted settings for the publisher-side vocal chain
  // (services/voiceChain.js): input gain, low-cut, compressor, gate, EC/NS
  // overrides. localStorage-only for now, shaped like the other per-user prefs
  // so a server-backed membership field can slot in beside the write later.
  const micProcessingStorageKey = useCallback(
    () => (tenantInfo ? `chatVoiceChain_${tenantInfo.tenantId}` : null),
    [tenantInfo]
  )
  const [micProcessing, setMicProcessing] = useState(DEFAULT_MIC_PROCESSING)
  // Ref mirror for plain closures on the publish path (acquirePublishStream /
  // withProcessedAudio) — same pattern as liveMicOnRef.
  const micProcessingRef = useRef(micProcessing)
  micProcessingRef.current = micProcessing
  // Hydrate once the tenant is known; unknown keys from older builds are
  // dropped by the merge, missing ones get defaults.
  useEffect(() => {
    const key = micProcessingStorageKey()
    if (!key) return
    try {
      const raw = localStorage.getItem(key)
      if (raw) {
        const saved = { ...DEFAULT_MIC_PROCESSING, ...JSON.parse(raw) }
        setMicProcessing(saved)
        voiceChain.updateSettings(saved)
      }
    } catch {
      /* corrupted/private mode — keep defaults */
    }
  }, [micProcessingStorageKey])

  // Point the per-remote-user volume store at this tenant's key.
  useEffect(() => {
    if (tenantInfo) remoteAudio.setStorageKey(`chatRemoteAudio_${tenantInfo.tenantId}`)
  }, [tenantInfo])

  // --- Per-user volume popover (the consumer-side chain's control surface) ---
  // One popover instance serves both summon points: the tile speaker button
  // (anchorEl) and the context menu's "Volume…" (anchorPosition).
  // remoteAudioPrefs mirrors remoteAudio's per-user settings purely for
  // RENDERING (icon state, slider value) — the manager is authoritative.
  const [volumePopover, setVolumePopover] = useState(null) // {userId, anchorEl?|anchorPosition?}
  const [remoteAudioPrefs, setRemoteAudioPrefs] = useState({}) // userId -> {volume, compressor}

  const mirrorRemotePrefs = (userId) =>
    setRemoteAudioPrefs((prev) => ({ ...prev, [userId]: remoteAudio.getSettings(userId) }))

  const handleOpenVolume = (userId, anchor) => {
    mirrorRemotePrefs(userId)
    // A DOM node anchors to the element; a {top,left} point (context menu)
    // anchors to the position.
    if (anchor && typeof anchor.top === "number") {
      setVolumePopover({ userId, anchorPosition: anchor })
    } else {
      setVolumePopover({ userId, anchorEl: anchor || null })
    }
  }

  const handleRemoteVolumeChange = (userId, pct) => {
    remoteAudio.setVolume(userId, pct)
    mirrorRemotePrefs(userId)
  }

  const handleRemoteCompressorToggle = (userId) => {
    remoteAudio.setCompressor(userId, !remoteAudio.getSettings(userId).compressor)
    mirrorRemotePrefs(userId)
  }

  // Connect to chat and load messages
  const connectToChat = useCallback(
    async (authData) => {
      if (!tenantInfo) return

      try {
        setIsConnecting(true)
        setConnectionError(null)

        // Ensure any existing connection is fully disconnected first
        if (socketService.isConnected) {
          console.log(
            "🔌 Disconnecting existing connection before reconnect..."
          )
          socketService.offAll()
          await socketService.disconnect()
        }

        // Small delay to ensure token is available and disconnection is complete
        await new Promise((resolve) => setTimeout(resolve, 200))

        // Load recent message history (best-effort). A failure here — e.g. a
        // transient 500 or the REST rate limiter — must NOT block the socket
        // connection, which is the part that actually matters. History will
        // backfill via live messages once connected.
        try {
          const messagesResponse = await messagesAPI.getMessages(
            tenantInfo.tenantId,
            tenantInfo.scriptHash,
            currentRoom
          )
          setMessages(messagesResponse.data.messages || [])
        } catch (historyError) {
          console.warn(
            "⚠️ Could not load message history (continuing to connect):",
            historyError.message
          )
        }

        // Connect to Socket.IO
        await socketService.connect(tenantInfo, authData)

        // Set up socket event listeners
        setupSocketListeners()

        // Join the chat room and WAIT for the server to confirm the join (so
        // socket.currentRoom is set) before any room-scoped media signaling.
        // The ack carries the current sesh snapshot (or null), so we know
        // atomically whether a sesh is active and can paint the right header on
        // first render — no button→status flicker. seshSynced gates the button
        // so it doesn't flash before we know.
        const joinAck = await socketService.joinChat(currentRoom)
        if (joinAck && Object.prototype.hasOwnProperty.call(joinAck, "sesh")) {
          setSesh(joinAck.sesh || null)
        }
        // Hydrate the ignore list from the join ack — the server-authoritative
        // source, correct even after a reload where the cached REST auth
        // membership predates an ignore change made in a prior session.
        if (joinAck && Object.prototype.hasOwnProperty.call(joinAck, "ignoredUserIds")) {
          setIgnoredUserIds(new Set(joinAck.ignoredUserIds || []))
        }
        if (joinAck && Object.prototype.hasOwnProperty.call(joinAck, "quickReplies")) {
          setQuickReplies(decodeQuickReplies(joinAck.quickReplies || []))
        }
        // Rehydrate profile fields from the server-authoritative join ack so a
        // reload shows the saved bio/banner (the cached REST auth membership can
        // predate a change made in a prior session).
        if (joinAck && Object.prototype.hasOwnProperty.call(joinAck, "bio")) {
          setProfileBio(joinAck.bio || '')
        }
        if (joinAck && Object.prototype.hasOwnProperty.call(joinAck, "bannerColor")) {
          setProfileBannerColor(joinAck.bannerColor || '')
        }
        setSeshSynced(true)

        // Now that the server has us in the room, flush any messages the user
        // typed while disconnected. Done HERE (post-join-ack) rather than on the
        // raw 'connect' so the server never rejects them with "Not in a room".
        socketService.flushPending()

        // Load my friend graph (best-effort — friends must never block the
        // connect). Skipped when the tenant has disabled friends (friendsEnabledRef
        // mirrors the config flag; default ON). Rehydrates the three Sets on every
        // (re)connect so a reload restores friend badges / request state, and
        // surfaces a single summary notice if requests piled up while I was away.
        if (friendsEnabledRef.current) {
          try {
            const fg = await socketService.getFriends()
            setFriendIds(new Set(fg.friends || []))
            setIncomingRequestIds(new Set(fg.incoming || []))
            setOutgoingRequestIds(new Set(fg.outgoing || []))
            const nIncoming = (fg.incoming || []).length
            if (nIncoming > 0) {
              addLocalSystemMessage(
                `You have ${nIncoming} pending friend request${nIncoming === 1 ? "" : "s"} — click a name in the sidebar to respond.`
              )
            }
          } catch (friendErr) {
            console.warn("⚠️ Could not load friends (continuing):", friendErr.message)
          }
        }

        // Seed the WebRTC device + the set of users already publishing video.
        // Best-effort: video must never block text chat. load() returns the
        // room's existing producers so their tiles can be offered immediately.
        //
        // Reset first: a reconnect lands on a server whose SFU state is gone
        // (mediasoup lives in the backend process), so any cached device /
        // transports here are stale and would fail. reset() is a safe no-op on a
        // truly-fresh connect (nothing loaded yet). We snapshot the publish
        // intent BEFORE reset (reset() doesn't touch the ref, but be explicit).
        const wantRepublish = publishIntentRef.current
        try {
          mediaService.reset()
          // Release the raw mic the vocal chain holds (reset() stopped the
          // processed track); the auto-republish below re-attaches fresh.
          voiceChain.detachSource()
          // All consumers died with the reset — drop their listening graphs;
          // re-watching (or the auto re-consume) re-attaches per user.
          remoteAudio.disposeAll()
          const existing = await mediaService.load()
          if (existing && existing.length) {
            // Split by kind: video drives producersByUser (the watch button /
            // has-webcam indicator); audio is tracked in the parallel ref so the
            // consume path can bundle it into the same tile.
            setProducersByUser((prev) => {
              const next = { ...prev }
              for (const p of existing) {
                if (p.kind === "audio") audioProducersByUserRef.current[p.userId] = p.producerId
                else next[p.userId] = p.producerId
              }
              return next
            })
          }

          // Auto-republish our own camera/mic if we were publishing before the
          // drop. The server lost our producer on restart, so without this the
          // user's video stays dead until they manually re-toggle. Best-effort —
          // a failure here must not fail the (already successful) reconnect.
          if (wantRepublish && enableWebcamRef.current) {
            console.log("🎬 Re-publishing webcam after reconnect")
            try {
              // Respect the user's CURRENT mute choice, not the value captured
              // when they first published (they may have muted since) — so a
              // deploy never silently un-mutes someone.
              await enableWebcamRef.current({ ...wantRepublish, micOn: liveMicOnRef.current })
            } catch (republishErr) {
              console.warn("⚠️ Auto-republish failed (camera off until re-toggled):", republishErr.message)
            }
          }
        } catch (mediaErr) {
          console.warn("⚠️ Media device init failed (video disabled):", mediaErr.message)
        }

        console.log("✅ Successfully connected to chat")

        // Stale-bundle guard: a (re)connect is exactly when a deploy has just
        // landed — compare our baked build stamp to the one the server now
        // serves and hard-reload ONCE if we're outdated, so nobody keeps
        // running old code against a new server. Fire-and-forget (never blocks
        // the connect); no-ops entirely unless the chat build actually changed.
        checkForNewBuild()

        return { ok: true }
      } catch (error) {
        console.error("❌ Failed to connect to chat:", error)

        // Distinguish an AUTH rejection from a transient failure. The backend's
        // socket middleware rejects with "Authentication error: ..." for an
        // expired/invalid token or a vanished user (e.g. a guest record gone
        // after a server restart). In that case the saved token is unusable —
        // clear it and drop cleanly back to the login screen rather than leaving
        // the user stranded in a chat shell that can never connect. Auth errors
        // are NOT retryable, so the reconnect machine must not back off on them.
        const msg = error?.message || ""
        const isAuthError = /authentication error|invalid token|jwt|unauthor/i.test(
          msg
        )
        if (isAuthError) {
          console.log("🔒 Socket auth rejected — clearing session, back to login")
          authStorage.clear()
          setAuthData(null)
          setUser(null)
          setIsAuthenticated(false)
          // Re-arm the post-auth auto-connect so the re-login can connect.
          setHasConnectedAfterAuth(false)
          setConnectionError("Your session has expired. Please log in again.")
          return { ok: false, authError: true }
        }
        // Transient failure (server down, timeout, network) — retryable.
        return { ok: false, authError: false, error: msg }
      } finally {
        setIsConnecting(false)
      }
    },
    [tenantInfo, currentRoom]
  )

  // --- Auto-reconnect machine ---------------------------------------------
  // Runs RECONNECT_DELAYS[index] as a backoff, attempts a connect, and on
  // failure schedules the next index. After the last delay fails it parks in the
  // 'failed' phase (manual Retry/Leave). A success clears everything.
  //   immediate=true skips the backoff delay for THIS attempt (used by manual
  //   Retry so a button click fires now) while keeping the displayed count.
  const runReconnectAttempt = useCallback(
    (index, immediate = false) => {
      // Out of scheduled attempts → park in the manual 'failed' phase. The modal
      // then offers Retry (retryReconnect → ONE more immediate attempt at this
      // same index, NOT a reset to 0) and Leave.
      if (index >= RECONNECT_DELAYS.length) {
        reconnectingRef.current = false
        setReconnect({ attempt: RECONNECT_DELAYS.length, phase: "failed" })
        return
      }

      // For a known server restart, the first attempt waits for the backend to
      // come back instead of firing at index-0's 0ms (which would just bounce
      // off a still-down server and burn the attempt). Later indices keep the
      // normal backoff.
      let delay = immediate ? 0 : RECONNECT_DELAYS[index]
      if (!immediate && index === 0 && maintenanceRef.current) {
        delay = MAINTENANCE_FIRST_DELAY_MS
      }
      // attempt number shown to the user is 1-based.
      setReconnect({ attempt: index + 1, phase: delay > 0 ? "waiting" : "connecting" })

      const attemptNow = async () => {
        // Bail if something resolved the situation while we waited (manual leave,
        // logout, an independent successful connect).
        if (!reconnectingRef.current) return
        setReconnect({ attempt: index + 1, phase: "connecting" })

        const result = await connectToChat(authData)

        if (result?.ok) {
          // Connected — tear down the reconnect UI.
          reconnectingRef.current = false
          maintenanceRef.current = false
          setIsMaintenance(false)
          setReconnect(null)
          setConnectionError(null)
          return
        }
        if (result?.authError) {
          // Token is dead; connectToChat already dropped us to login. Stop.
          reconnectingRef.current = false
          maintenanceRef.current = false
          setIsMaintenance(false)
          setReconnect(null)
          return
        }
        // Transient failure — schedule the next backoff step (always delayed).
        runReconnectAttempt(index + 1)
      }

      if (delay > 0) {
        reconnectTimerRef.current = setTimeout(attemptNow, delay)
      } else {
        attemptNow()
      }
    },
    [authData, connectToChat]
  )

  // Begin a fresh reconnect sequence from the first delay. Guarded so a second
  // trigger (e.g. a disconnect firing mid-sequence) doesn't start a parallel loop.
  // maintenance=true (a known deploy) makes the first attempt wait for the
  // server to return and switches the modal to calm "updating" copy.
  const startReconnect = useCallback((maintenance = false) => {
    if (reconnectingRef.current) return
    if (!authData) return
    reconnectingRef.current = true
    maintenanceRef.current = maintenance
    setIsMaintenance(maintenance)
    clearTimeout(reconnectTimerRef.current)
    runReconnectAttempt(0)
  }, [authData, runReconnectAttempt])

  // Manual Retry from the 'failed' modal: one more attempt WITHOUT resetting the
  // counter (per design — it consumes the final try rather than starting over).
  // Fires IMMEDIATELY (no 60s wait) since the user just clicked; success clears,
  // failure parks back in 'failed'.
  const retryReconnect = useCallback(() => {
    if (reconnectingRef.current) return
    reconnectingRef.current = true
    clearTimeout(reconnectTimerRef.current)
    runReconnectAttempt(RECONNECT_DELAYS.length - 1, true)
  }, [runReconnectAttempt])

  // Leave from the reconnect modal: not a true logout (we're already
  // disconnected) — just abandon retries and return to the instance auth home.
  const leaveReconnect = useCallback(() => {
    reconnectingRef.current = false
    clearTimeout(reconnectTimerRef.current)
    setReconnect(null)
    mediaService.reset()
    voiceChain.dispose()
    remoteAudio.disposeAll()
    socketService.offAll()
    socketService.disconnect()
    setIsAuthenticated(false)
    setUser(null)
    setConnectionError(null)
    setMessages([])
    setOnlineUsers([])
    setProducersByUser({})
    setConsumedStreams({})
    setWatchingUsers([])
    setWatchedByUsers([])
    setBlockedWatchers([])
    setBlockedByUsers([])
    setSelfStream(null)
    setIsPublishing(false)
    setSesh(null) // drop any active sesh banner when leaving
    setSeshSynced(false) // re-gate the button until the next join ack re-syncs
    // Re-arm the post-auth auto-connect so a later re-login can connect.
    setHasConnectedAfterAuth(false)
    resetImState()
  }, [resetImState])

  // Acknowledge the "you were kicked" modal: media + most session state were
  // already torn down when the notice arrived (onKicked); here we clear the
  // remaining chat state, drop the socket, and bounce to the instance auth home.
  const handleKickedDismiss = useCallback(() => {
    setKickedBy(null)
    socketService.offAll()
    socketService.disconnect()
    setIsAuthenticated(false)
    setUser(null)
    setConnectionError(null)
    setMessages([])
    setOnlineUsers([])
    // Re-arm the post-auth auto-connect so a later re-login can connect.
    setHasConnectedAfterAuth(false)
  }, [])

  // "I'm still here" — emit an immediate activity ping, bypassing the hook's
  // throttle. The banner is NOT hidden optimistically: it goes away when the
  // server sends idle:warning-cleared, so the server stays the single source of
  // truth and the banner also clears if the user answers by simply typing. The
  // transient "confirming" state just shows the click registered; if the ping is
  // lost, it re-enables so they can try again.
  const handleStillHere = useCallback(() => {
    setIdleConfirming(true)
    idlePingNow()
    setTimeout(() => setIdleConfirming(false), 3000)
  }, [idlePingNow])

  // Rejoin after an inactivity disconnect. The session was never dropped, so
  // this is just re-arming the post-auth auto-connect effect — the same path a
  // page reload would take, minus the reload.
  const handleIdleRejoin = useCallback(() => {
    setIdleKicked(false)
    setIdleDeadline(null)
    setConnectionError(null)
    socketService.offAll() // stale listeners bound to the dead socket
    socketService.disconnect() // no-op if already closed; resets intentional flag
    setHasConnectedAfterAuth(false) // <- re-arms the auto-connect effect
  }, [])

  // Set up Socket.IO event listeners
  const setupSocketListeners = () => {
    // New message received
    socketService.onNewMessage((message) => {
      console.log("📨 New message received:", message)
      setMessages((prev) => {
        // Check if message already exists to prevent duplicates
        if (prev.find((m) => m.id === message.id)) {
          return prev
        }
        // Ding if this NEW message mentions me — only on first delivery (inside
        // the dedupe guard, so a re-delivered duplicate doesn't ding twice),
        // never for my own messages, and only when the sound isn't 'off'. Read
        // my id from socketService.authData (this listener is registered once,
        // so closing over `user` would capture a stale value).
        const myId = socketService.authData?.user?.id
        const fromMe = myId != null && String(message.user?.id) === String(myId)
        // Ignore / Mute: an ignored user's mention pings me neither with sound
        // (here) nor with a highlight (the message is filtered out at render in
        // ChatPanel, so no pill renders).
        const fromIgnored = ignoredUserIdsRef.current.has(message.user?.id)
        if (
          !fromMe &&
          !fromIgnored &&
          mentionSoundRef.current !== "off" &&
          containsMentionOf(message.content, myId)
        ) {
          playMentionSound(mentionSoundRef.current, mentionVolumeRef.current)
        }
        // Add new message, bounding the in-memory transcript.
        const updated = [...prev, message].slice(-MAX_MESSAGES)
        return updated
      })
    })

    // User joined the room
    socketService.onUserJoined((data) => {
      console.log("👋 User joined:", data.user.username)
      // Add the user to the online users list
      setOnlineUsers((prev) => {
        // Check if user already exists to prevent duplicates
        if (prev.find((u) => u.id === data.user.id)) {
          return prev
        }
        return [...prev, { ...data.user, isOnline: true }]
      })
    })

    // User left the room
    socketService.onUserLeft((data) => {
      console.log("👋 User left:", data.username)
      // Remove the user from the online users list
      setOnlineUsers((prev) => prev.filter((u) => u.id !== data.userId))
    })

    // Room users update
    socketService.onRoomUsers((users) => {
      console.log("👥 Room users updated:", users)
      setOnlineUsers(users)
      // Sync my OWN avatar from the server's truth. On a fresh reconnect the
      // TopBar renders the `user` object re-hydrated from cached auth, which can
      // be stale (e.g. the avatar was set in an earlier session, or before the
      // cache learned about it). My entry in room-users carries the DB avatar, so
      // adopt it here — keeping the header correct without a full re-login.
      const myId = socketService.authData?.user?.id
      const me = myId && users.find((u) => String(u.id) === String(myId))
      if (me?.avatar) {
        setUser((u) =>
          u && u.avatar !== me.avatar ? { ...u, avatar: me.avatar } : u
        )
      }
    })

    // Patch every place a user's avatar is shown when it changes. Shared by the
    // self-ack (avatar-updated) and the broadcast for others (user-avatar-updated)
    // so a change shows live everywhere — sidebar list + that user's already-
    // rendered messages — without a reload. Stores the raw "/uploads/..." path;
    // render sites resolve it against the backend via resolveAvatarUrl.
    const applyAvatarUpdate = (userId, avatar) => {
      if (!userId || !avatar) return
      setOnlineUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, avatar } : u))
      )
      setMessages((prev) =>
        prev.map((m) =>
          m.user?.id === userId
            ? { ...m, user: { ...m.user, avatar } }
            : m
        )
      )
      // If it's me, update the header/self user object too — and persist the new
      // avatar into the cached auth (in-memory + localStorage) under the canonical
      // `avatarUrl` key. Without this, a reload re-hydrates from authStorage, whose
      // user object still has the pre-upload avatar, so the photo silently vanishes
      // everywhere (header + Settings dialog) until a full re-login. (The DB is
      // already updated server-side; this just keeps the cached session in sync.)
      if (String(userId) === String(socketService.authData?.user?.id)) {
        setUser((u) => (u ? { ...u, avatar } : u))
        if (socketService.authData?.user) {
          socketService.authData.user.avatarUrl = avatar
        }
        const savedAuth = authStorage.load()
        if (savedAuth?.user) {
          authStorage.save({
            ...savedAuth,
            user: { ...savedAuth.user, avatarUrl: avatar },
          })
        }
      }
    }
    socketService.onAvatarUpdated(({ avatarUrl }) => {
      console.log("🖼️ My avatar updated:", avatarUrl)
      applyAvatarUpdate(socketService.authData?.user?.id, avatarUrl)
    })
    socketService.onUserAvatarUpdated(({ userId, avatar }) => {
      console.log("🖼️ User avatar updated:", userId, avatar)
      applyAvatarUpdate(userId, avatar)
    })

    // Socket errors
    socketService.onError((error) => {
      console.error("🔌 Socket error:", error)
      setConnectionError(error.message)
    })

    // Handle connection displacement (kicked by another tab)
    socketService.onDisplaced((data) => {
      console.log("⚡ Connection displaced:", data.reason)
      // Release the camera + all media (transports/consumers) — otherwise the
      // displaced tab keeps the webcam light on and leaks SFU resources.
      mediaService.reset()
      voiceChain.dispose() // releases the raw mic the chain holds
      remoteAudio.disposeAll()
      pendingWatchRef.current.clear()
      queuedWatchTargetsRef.current.clear()
      // Kill the auto-reconnect machine (mirrors onKicked). A pending backoff
      // timer here would reconnect with the OLD auth right after the user logs
      // back in — displacing their fresh session in an endless loop.
      reconnectingRef.current = false
      clearTimeout(reconnectTimerRef.current)
      setReconnect(null)
      // Re-arm the post-auth auto-connect for the next login from this screen.
      setHasConnectedAfterAuth(false)
      setConnectionError("Session ended: " + data.reason)
      setIsAuthenticated(false)
      setUser(null)
      setProducersByUser({})
      setConsumedStreams({})
      setWatchingUsers([])
      setWatchedByUsers([])
      setBlockedWatchers([])
      setBlockedByUsers([])
      setSelfStream(null)
      setIsPublishing(false)
      setSesh(null) // a displaced session has no live sesh to show
      setSeshSynced(false)
      resetImState()
      socketService.disconnect()
    })

    // An admin kicked us out of the room. The server has already sent this notice
    // and is closing our socket (the ensuing 'io server disconnect' is excluded
    // from the auto-reconnect machine, so no reconnect modal fights this one).
    // Tear down media like a displaced session, but keep the chat shell mounted
    // behind the terminal modal — the bounce to the home screen happens when the
    // user dismisses it (handleKickedDismiss).
    socketService.onKicked((data) => {
      console.log("👢 Kicked by admin:", data?.by)
      mediaService.reset()
      voiceChain.dispose() // releases the raw mic the chain holds
      remoteAudio.disposeAll()
      pendingWatchRef.current.clear()
      queuedWatchTargetsRef.current.clear()
      // Stop the auto-reconnect machine from spinning up on the imminent drop.
      reconnectingRef.current = false
      clearTimeout(reconnectTimerRef.current)
      setReconnect(null)
      setProducersByUser({})
      setConsumedStreams({})
      setWatchingUsers([])
      setWatchedByUsers([])
      setBlockedWatchers([])
      setBlockedByUsers([])
      setSelfStream(null)
      setIsPublishing(false)
      setSesh(null)
      setSeshSynced(false)
      resetImState()
      setKickedBy(data?.by || "an admin")
    })

    // ---- AFK idle policy ----------------------------------------------
    // Derive the deadline from the DURATION the server sent, using OUR clock.
    // Never store a server absolute timestamp: a skewed local clock would
    // render a negative or wildly wrong countdown.
    socketService.onIdleWarning((data) => {
      const graceMs = Number(data?.graceMs) || 60000
      setIdleDeadline(Date.now() + graceMs)
      setIdleConfirming(false)
    })
    socketService.onIdleWarningCleared(() => {
      setIdleDeadline(null)
      setIdleConfirming(false)
    })

    // Disconnected for inactivity. Same teardown as onKicked, with two
    // deliberate differences: the session is KEPT (no setIsAuthenticated(false),
    // no authStorage.clear) and hasConnectedAfterAuth is left TRUE — flipping it
    // is what the Rejoin button does, and doing it here would instantly
    // reconnect and undo the kick.
    socketService.onIdleKicked((data) => {
      console.log("💤 Disconnected for inactivity after", data?.idleMinutes, "min")
      setIdleDeadline(null)
      // Load-bearing: connectToChat re-publishes the camera on reconnect
      // whenever publishIntentRef is non-null. Without this, Rejoin silently
      // turns the webcam back on and re-creates the problem we just solved.
      publishIntentRef.current = null
      mediaService.reset()
      voiceChain.dispose() // releases the raw mic the chain holds
      remoteAudio.disposeAll()
      pendingWatchRef.current.clear()
      queuedWatchTargetsRef.current.clear()
      // Stop the auto-reconnect machine from spinning up on the imminent drop.
      reconnectingRef.current = false
      clearTimeout(reconnectTimerRef.current)
      setReconnect(null)
      setProducersByUser({})
      setConsumedStreams({})
      setWatchingUsers([])
      setWatchedByUsers([])
      setBlockedWatchers([])
      setBlockedByUsers([])
      setSelfStream(null)
      setIsPublishing(false)
      setSesh(null)
      setSeshSynced(false)
      resetImState()
      setIdleKicked(true)
    })

    // Someone sent ME a friend request. Move them into incomingRequestIds (so
    // the sidebar shows the attention marker + the menu flips to Accept/Decline)
    // and drop a system nudge so "click a name to respond" has a pointer.
    socketService.onFriendRequestReceived((data) => {
      const fromId = data?.fromUserId
      if (!fromId) return
      setIncomingRequestIds((prev) => {
        const next = new Set(prev)
        next.add(fromId)
        return next
      })
      addLocalSystemMessage(
        `${data.fromUsername || "Someone"} wants to add you as a friend — click their name to respond.`
      )
    })

    // A request I sent was resolved. accepted → they become a friend + a system
    // message; declined → the request just disappears (silent, "no drama").
    // Either way clear them from outgoingRequestIds.
    socketService.onFriendRequestResolved((data) => {
      const themId = data?.userId
      if (!themId) return
      setOutgoingRequestIds((prev) => {
        if (!prev.has(themId)) return prev
        const next = new Set(prev)
        next.delete(themId)
        return next
      })
      if (data.accepted) {
        setFriendIds((prev) => {
          const next = new Set(prev)
          next.add(themId)
          return next
        })
        // Prefer the username the server stamped on the push (the resolver's
        // name) — onlineUsers here is a stale closure from listener-registration
        // and may not yet include a just-joined accepter.
        const name = data.username || onlineUsers.find((u) => u.id === themId)?.username || "Someone"
        addLocalSystemMessage(`${name} accepted your friend request.`)
      }
    })

    // A user started publishing a producer. kind splits the routing: an audio
    // producer just gets recorded in the parallel ref (so a later consume can
    // bundle it); a video producer drives producersByUser (the watch button).
    socketService.onNewProducer((data) => {
      console.log("🎥 New producer:", data.username, data.producerId, data.kind)
      if (data.kind === "audio") {
        audioProducersByUserRef.current[data.userId] = data.producerId
        return
      }
      setProducersByUser((prev) => ({ ...prev, [data.userId]: data.producerId }))
    })

    // A user stopped publishing (disabled webcam / disconnected). Fires once per
    // producer, so for a cam+mic publisher we get two events (video + audio).
    socketService.onProducerClosed((data) => {
      console.log("🛑 Producer closed for user:", data.userId)
      // Always release the consumer for THIS producer id.
      mediaService.stopConsuming(data.producerId)
      // If it was the user's audio producer, just drop it from the ref — no
      // video-tile state to touch. Tear down their listening graph explicitly:
      // a locally-closed consumer stops its track WITHOUT firing 'ended'. The
      // record stays so a later audio producer can re-attach.
      if (audioProducersByUserRef.current[data.userId] === data.producerId) {
        delete audioProducersByUserRef.current[data.userId]
        remoteAudio.detachAudio(data.userId)
        return
      }
      // Only clear the user's video state if the closed producer is the one we
      // currently track for them. A user with two tabs can have one socket's
      // producer close while another tab is still publishing a DIFFERENT
      // producer — keying purely by userId would wrongly drop the live tile.
      setProducersByUser((prev) => {
        if (prev[data.userId] !== data.producerId) return prev
        const next = { ...prev }
        delete next[data.userId]
        return next
      })
      setConsumedStreams((prev) => {
        if (!(data.userId in prev)) return prev
        const next = { ...prev }
        delete next[data.userId]
        return next
      })
      // Their tile is gone — drop the listening chain with it.
      remoteAudio.detach(data.userId)
      // Stop "watching" them so the tile doesn't linger as a "connecting…"
      // placeholder; the user can re-watch if they republish. Any queued-for-
      // approval flag is moot too — the producer it was for is gone.
      pendingWatchRef.current.delete(data.userId)
      queuedWatchTargetsRef.current.delete(data.userId)
      setWatchingUsers((prev) => prev.filter((id) => id !== data.userId))
      // Clear any stale "video paused" flag — the producer is gone, not paused.
      setPausedVideoUsers((prev) => prev.filter((id) => id !== data.userId))
    })

    // A publisher paused/resumed a producer (their cam/mic toggle). For VIDEO,
    // flip the watcher-side "paused" flag so the tile shows a placeholder rather
    // than a frozen frame. Audio pauses need no UI on the watcher (just silence).
    socketService.onProducerPaused((data) => {
      if (data.kind !== "video") return
      setPausedVideoUsers((prev) => {
        const has = prev.includes(data.userId)
        if (data.paused) return has ? prev : [...prev, data.userId]
        return has ? prev.filter((id) => id !== data.userId) : prev
      })
    })

    // Someone started watching MY cam -> light their "they watch me" eye.
    socketService.onWatcherAdded((data) => {
      console.log("👁️ Now being watched by:", data.watcherUserId)
      setWatchedByUsers((prev) =>
        prev.includes(data.watcherUserId) ? prev : [...prev, data.watcherUserId]
      )
    })

    // Someone stopped watching MY cam -> turn their eye off.
    socketService.onWatcherRemoved((data) => {
      console.log("👁️ No longer watched by:", data.watcherUserId)
      setWatchedByUsers((prev) => prev.filter((id) => id !== data.watcherUserId))
    })

    // A publisher BLOCKED me from their cam. The consumer teardown (stream/tile/
    // watchingUsers) is handled by the rtc:producer-closed the server sends just
    // before this when a session was active; here we just hide their watch
    // button by marking them as having blocked me.
    socketService.onCamBlocked((data) => {
      console.log("🚫 Blocked from watching:", data.ownerUserId)
      setBlockedByUsers((prev) =>
        prev.includes(data.ownerUserId) ? prev : [...prev, data.ownerUserId]
      )
      // Belt-and-suspenders: drop any lingering watch state for them.
      pendingWatchRef.current.delete(data.ownerUserId)
      setWatchingUsers((prev) => prev.filter((id) => id !== data.ownerUserId))
    })

    // A publisher UNBLOCKED me -> their watch button reappears.
    socketService.onCamUnblocked((data) => {
      console.log("✅ Unblocked from watching:", data.ownerUserId)
      setBlockedByUsers((prev) => prev.filter((id) => id !== data.ownerUserId))
    })

    // Publishing stopped on its own (camera track ended outside our control) —
    // sync React state so the dead self tile is dropped and eyes clear.
    mediaService.onUnpublished = () => {
      console.log("🛑 Publishing ended (track stopped externally)")
      // External-death path (camera unplugged etc.) — release the raw mic the
      // vocal chain is holding, same as an explicit disable.
      voiceChain.detachSource()
      setIsPublishing(false)
      setWatchedByUsers([])
      setBlockedWatchers([]) // no cam to block anyone from anymore
      setSelfStream(null) // local self-monitor preview is gone with the camera
      // Drop the self grid tile + its consumed stream if I was watching myself.
      setWatchingUsers((prev) => prev.filter((id) => id !== user?.id))
      setConsumedStreams((prev) => {
        const next = { ...prev }
        if (user?.id) delete next[user.id]
        return next
      })
    }

    // Someone REQUESTED to watch my cam. Public mode auto-accepts; private mode
    // queues the request for an explicit accept/deny via the approval banner.
    // Reads webcamModeRef (not webcamMode) — this listener is registered once,
    // so the captured webcamMode would be stale.
    socketService.onWebcamRequest((data) => {
      // If this request is the one fired by the user ACCEPTING a cam invite I
      // sent them, honor the pre-acceptance: auto-accept exactly once and burn
      // the token. Both the viaInvite flag AND an outstanding token are required,
      // so a manual cam-icon click (viaInvite:false) can never be auto-accepted.
      if (data.viaInvite && outstandingInvitesRef.current.has(data.fromUserId)) {
        outstandingInvitesRef.current.delete(data.fromUserId)
        console.log("✅ Invite accepted — auto-accepting watch request from:", data.fromUsername)
        socketService.acceptWebcam(data.fromUserId).catch((err) =>
          console.error("❌ Failed to auto-accept invited watch request:", err.message)
        )
        return
      }
      if (webcamModeRef.current === 'private') {
        console.log("🔒 Private mode — prompting to accept watch request from:", data.fromUsername)
        setPendingCamRequests((prev) =>
          prev.some((r) => r.fromUserId === data.fromUserId)
            ? prev
            : [...prev, { fromUserId: data.fromUserId, fromUsername: data.fromUsername }]
        )
        // Ack the requester that their request is queued behind my approval —
        // this is what triggers their "You requested…" notice (they can't know
        // my mode otherwise). Duplicate requests are already blocked on their
        // side by pendingWatchRef, so no dedup needed here.
        socketService.queuedWebcam(data.fromUserId).catch((err) =>
          console.error("❌ Failed to send webcam-queued ack:", err.message)
        )
        return
      }
      console.log("📨 Webcam requested by:", data.fromUsername, "(auto-accepting)")
      socketService.acceptWebcam(data.fromUserId).catch((err) =>
        console.error("❌ Failed to accept webcam request:", err.message)
      )
    })

    // A PRIVATE publisher queued my request for manual approval. Only now do we
    // print "You requested…" — at click time we can't know the publisher's mode
    // (it lives solely in their client), and printing it for a public publisher
    // who instantly auto-accepts read like a bogus privacy prompt. Deliberately
    // no "waiting for approval" phrasing: the publisher may ignore the request
    // forever, and the plain past-tense line stays truthful either way.
    socketService.onWebcamQueued((data) => {
      const userId = data.fromUserId
      console.log("⏳ Webcam request queued for approval by:", userId)
      queuedWatchTargetsRef.current.add(userId)
      addLocalSystemMessage(`You requested ${data.fromUsername || "this user"}'s webcam.`)
    })

    // A user ACCEPTED my webcam request -> now actually consume their producer
    // and attach the stream. Resolve the producer id from the ref so we read
    // the current map, not the snapshot from when this listener was registered.
    socketService.onWebcamAccept(async (data) => {
      const userId = data.fromUserId
      console.log("✅ Webcam request accepted by:", userId)
      // The request has resolved — clear the in-flight guard either way.
      pendingWatchRef.current.delete(userId)
      // Was this a real approval (the publisher was private and queued it) or a
      // public-cam auto-accept? A real approval always gets the "You are now
      // watching…" notice; an auto-accept only shows it if the tenant's
      // watchAck setting is on. Check-and-clear the flag here.
      const wasQueued = queuedWatchTargetsRef.current.delete(userId)
      const producerId = producersByUserRef.current[userId]
      if (!producerId) {
        console.warn("Accept received but no producer for user:", userId)
        setWatchingUsers((prev) => prev.filter((id) => id !== userId))
        return
      }
      try {
        // Bundle the user's audio (if they're publishing a mic) into the same
        // tile stream as their video.
        const audioProducerId = audioProducersByUserRef.current[userId] || null
        const stream = await mediaService.consume(producerId, "default", audioProducerId)
        // Route their audio through the per-user listening chain (volume boost /
        // compressor / limiter) — the tile plays the returned PLAYBACK stream
        // (video + processed audio), so native controls keep working. Falls
        // back to the raw stream if Web Audio is unavailable. Never for the
        // self tile (that path is video-only via consumeSelf, but guard anyway).
        const playback =
          userId !== user?.id ? remoteAudio.attach(userId, stream) : stream
        // Add to watchingUsers only now (no optimistic add) so the tile appears
        // exactly when the publisher accepts — never a stuck "connecting…" tile.
        setWatchingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]))
        setConsumedStreams((prev) => ({ ...prev, [userId]: playback }))
        if (wasQueued || watchAckEnabledRef.current) {
          addLocalSystemMessage(`You are now watching ${data.fromUsername || "this user"}.`)
        }
      } catch (err) {
        console.error("❌ Failed to consume after accept:", err.message)
        setWatchingUsers((prev) => prev.filter((id) => id !== userId))
      }
    })

    // A publisher DENIED my webcam request -> clear the in-flight guard, show a
    // denial notice, and start an anti-spam cooldown so I can't immediately
    // re-request (their cam icon dims + disables until it expires).
    socketService.onWebcamDeny((data) => {
      const userId = data.fromUserId
      console.log("🚫 Webcam request denied by:", userId)
      pendingWatchRef.current.delete(userId)
      queuedWatchTargetsRef.current.delete(userId)
      addLocalSystemMessage(`Sorry, ${data.fromUsername || "this user"} denied the private chat.`)
      setDeniedCooldownUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]))
      setTimeout(() => {
        setDeniedCooldownUsers((prev) => prev.filter((id) => id !== userId))
      }, CAM_REQUEST_COOLDOWN_MS)
    })

    // A private publisher INVITED me to view their cam -> queue it in the same
    // banner as incoming requests, flagged as an invite so the copy + buttons
    // (Accept/Deny/Ignore) render differently. Accepting fires a viaInvite watch
    // request the publisher auto-accepts (see handleAcceptCamInvite).
    socketService.onWebcamInvite((data) => {
      console.log("📩 Cam invite from:", data.fromUsername)
      setPendingCamInvites((prev) =>
        prev.some((r) => r.fromUserId === data.fromUserId)
          ? prev
          : [...prev, { fromUserId: data.fromUserId, fromUsername: data.fromUsername }]
      )
    })

    // An invitee DECLINED my cam invite -> drop my outstanding pre-acceptance
    // token (so a later request from them isn't silently auto-accepted) and show
    // a notice.
    socketService.onWebcamInviteDeny((data) => {
      const userId = data.fromUserId
      console.log("🚫 Cam invite declined by:", userId)
      outstandingInvitesRef.current.delete(userId)
      addLocalSystemMessage(`${data.fromUsername || "This user"} declined your cam invite.`)
    })

    // --- Peer-to-peer file transfer listeners ------------------------------

    // Someone OFFERS me a file -> queue it for the Accept/Deny banner. Dedup by
    // transferId so a resend can't stack duplicate banners.
    socketService.onFileOffer((data) => {
      console.log("📎 File offer from:", data.fromUsername, data.name)
      setPendingFileOffers((prev) =>
        prev.some((o) => o.transferId === data.transferId)
          ? prev
          : [...prev, {
              fromUserId: data.fromUserId,
              fromUsername: data.fromUsername,
              transferId: data.transferId,
              name: data.name,
              size: data.size,
              mime: data.mime,
            }]
      )
    })

    // The recipient ACCEPTED my offer -> stream the held File's bytes, updating
    // a send-progress entry as we go, then drop the held file.
    socketService.onFileAccept(async (data) => {
      const { fromUserId, fromUsername, transferId } = data
      const file = outgoingFilesRef.current.get(transferId)
      if (!file) {
        console.warn("File accept for unknown/expired transfer:", transferId)
        return
      }
      setFileTransfers((prev) => ({
        ...prev,
        [transferId]: { dir: "send", peerUsername: fromUsername, name: file.name, size: file.size, sent: 0 },
      }))
      try {
        await socketService.sendFileData(fromUserId, transferId, file, {
          onProgress: (sent, total) => {
            setFileTransfers((prev) =>
              prev[transferId] ? { ...prev, [transferId]: { ...prev[transferId], sent, size: total } } : prev
            )
          },
        })
        addLocalSystemMessage(`Sent "${file.name}" to ${fromUsername || "this user"}.`)
      } catch (err) {
        console.error("❌ File send failed:", err.message)
        socketService.cancelFile(fromUserId, transferId, "send-error")
        addLocalSystemMessage(`Failed to send "${file.name}".`)
      } finally {
        outgoingFilesRef.current.delete(transferId)
        clearFileTransfer(transferId)
      }
    })

    // The recipient DENIED my offer -> drop the held file + notice.
    socketService.onFileDeny((data) => {
      const file = outgoingFilesRef.current.get(data.transferId)
      outgoingFilesRef.current.delete(data.transferId)
      addLocalSystemMessage(
        `${data.fromUsername || "This user"} declined the file${file ? ` "${file.name}"` : ""}.`
      )
    })

    // A relayed CHUNK of a file I accepted -> buffer it; on the last chunk,
    // assemble the Blob and trigger a download. Buffers live in a ref so binary
    // data never re-renders the chat.
    socketService.onFileChunk((data) => {
      const { transferId, chunk, last } = data
      const entry = incomingFilesRef.current.get(transferId)
      if (!entry) return // not an accepted transfer (or already torn down)
      // Decode this base64 chunk to bytes and stash it.
      if (chunk) {
        const bin = atob(chunk)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
        entry.chunks.push(bytes)
        entry.received += bytes.length
      }
      setFileTransfers((prev) =>
        prev[transferId]
          ? { ...prev, [transferId]: { ...prev[transferId], sent: entry.received } }
          : prev
      )
      if (last) {
        saveReceivedFile(transferId)
      }
    })

    // The other side ABORTED (cancel, send-error, or peer-left) -> tear down
    // whichever side I'm on and notify.
    socketService.onFileCancel((data) => {
      const { transferId, reason } = data
      const incoming = incomingFilesRef.current.get(transferId)
      const outgoing = outgoingFilesRef.current.get(transferId)
      incomingFilesRef.current.delete(transferId)
      outgoingFilesRef.current.delete(transferId)
      setPendingFileOffers((prev) => prev.filter((o) => o.transferId !== transferId))
      clearFileTransfer(transferId)
      if (incoming || outgoing) {
        const name = incoming?.meta?.name || outgoing?.name || "file"
        addLocalSystemMessage(
          reason === "peer-left"
            ? `File transfer of "${name}" was interrupted (the other user left).`
            : `File transfer of "${name}" was cancelled.`
        )
      }
    })

    // --- Private messaging (IM mode) listeners ----------------------------
    // The backend relays private messages (never DB-stored) as receive-private
    // (to the recipient) + private-sent (echo to the sender). We ingest both
    // into the per-partner conversation buffers; ingestPrivateMessage opens a
    // managed IM window (windowing/) for the partner and the WindowLayer renders
    // the conversation. An incoming message always pops a window.
    socketService.onPrivateMessage((pm) => {
      console.log("💬 Private message received:", pm)
      ingestPrivateMessage(pm, false)
    })
    socketService.onPrivateSent((pm) => {
      // My own message, echoed back by the server — render it as a "mine" line.
      ingestPrivateMessage(pm, true)
    })

    // A private-chat peer is typing (or stopped). Reflect it on that
    // conversation, and auto-clear after a short gap in case a stop event is
    // lost. Read the live focused id from state via the functional updater.
    socketService.onPrivateTyping((data) => {
      const { fromUserId, isTyping } = data || {}
      if (!fromUserId) return
      setImConversations((prev) => {
        const conv = prev[fromUserId]
        if (!conv) return prev // no window/history for them yet — ignore
        return { ...prev, [fromUserId]: { ...conv, typing: !!isTyping } }
      })
      clearTimeout(imTypingTimersRef.current[fromUserId])
      if (isTyping) {
        imTypingTimersRef.current[fromUserId] = setTimeout(() => {
          setImConversations((prev) =>
            prev[fromUserId]
              ? { ...prev, [fromUserId]: { ...prev[fromUserId], typing: false } }
              : prev
          )
        }, 6000)
      }
    })

    // --- Group "Sesh" Countdown listeners ---------------------------------
    // The server owns the clock; we just store whatever snapshot/tick it sends
    // and let SeshBanner render it. The displayed seconds always come from the
    // server (no client-side countdown), so a late joiner and everyone else stay
    // in lockstep. The start/end SYSTEM chat notices are server-broadcast
    // new-message events — already rendered by ChatPanel — so we do NOT post
    // them locally (would double up).

    // A sesh started (phase=join) — full snapshot incl. the auto-joined initiator.
    socketService.onSeshStart((state) => {
      console.log("🔥 Sesh started:", state.initiator?.username)
      setSesh(state)
    })

    // Late-join snapshot for THIS client (sent on join-chat when a sesh is
    // already running) — render mid-countdown at the correct remaining time.
    socketService.onSeshState((state) => {
      console.log("🔥 Sesh state (late-join sync):", state.phase, state.secondsLeft)
      setSesh(state)
    })

    // One-second authoritative tick during JOIN/PREP — merge the new
    // phase/secondsLeft onto the existing snapshot (keep smokers/initiator).
    socketService.onSeshTick((data) => {
      setSesh((prev) =>
        prev ? { ...prev, phase: data.phase, secondsLeft: data.secondsLeft } : prev
      )
    })

    // Entered the PREP phase (no more joins) — full snapshot (secondsLeft=10).
    socketService.onSeshPrep((state) => {
      console.log("🔥 Sesh prep phase")
      setSesh(state)
    })

    // Live smoker list changed (someone joined) — update just the smokers.
    socketService.onSeshSmokers((data) => {
      setSesh((prev) => (prev ? { ...prev, smokers: data.smokers } : prev))
    })

    // TAKE A HIT — countdown hit zero. Show the blinking "Take a hit!" banner
    // for the server's hit window, then clear locally. The server deletes the
    // sesh after the same window and sends no further events, so this local
    // timeout is what returns us to idle. SESH_HIT_CLEAR_MS is a touch longer
    // than the server's HIT window to avoid clearing a frame early.
    socketService.onSeshDone((state) => {
      console.log("🔥 Take a hit!")
      setSesh(state)
      setTimeout(() => {
        // Only clear if we're still showing THIS done state (a brand-new sesh
        // could have started in the meantime — don't clobber it).
        setSesh((prev) => (prev && prev.phase === "done" ? null : prev))
      }, SESH_HIT_CLEAR_MS)
    })

    // Someone NOODLED — drop the jester hat on them until the 420/350 they
    // fumbled arrives. Fired on the noodle (room-wide) and re-sent to us on a
    // late join if a shame is still active. applyNoodleShame arms the lift timer.
    socketService.onSeshNoodleShame((entry) => {
      console.log("🍜 Noodle shame:", entry?.username)
      applyNoodleShame(entry)
    })

    // Live head transform from the current noodler (they run the tracker on their
    // own camera; the server relays it). Store it so the noodler's video tile can
    // pin the hat to their head. `gone` (face lost) or a mismatched userId clears
    // it → the tile falls back to the static hat.
    socketService.onSeshNoodleTrack((data) => {
      if (!data || data.gone) { setNoodleTrack(null); return }
      setNoodleTrack({
        userId: data.userId,
        cx: data.cx, cy: data.cy, wFrac: data.wFrac, rollDeg: data.rollDeg,
      })
    })
  }

  // Keep the ref in sync so once-registered socket listeners read the latest
  // producer map.
  useEffect(() => {
    producersByUserRef.current = producersByUser
  }, [producersByUser])

  // Keep webcamModeRef current for the once-registered onWebcamRequest listener.
  useEffect(() => {
    webcamModeRef.current = webcamMode
  }, [webcamMode])

  useEffect(() => {
    if (
      isAuthenticated &&
      tenantInfo &&
      user &&
      !isConnecting &&
      // An inactivity disconnect keeps the session authenticated, so without
      // this guard the effect would immediately reconnect and undo the kick.
      // Clearing idleKicked is exactly what the Rejoin button does.
      !idleKicked &&
      !hasConnectedAfterAuth
    ) {
      // Use the in-memory auth payload (also mirrored in localStorage, so a
      // reload re-hydrates it via authStorage.load() on mount).
      if (authData && !socketService.isConnected) {
        console.log("🔄 Auto-connecting to existing session...")

        let cancelled = false

        // Initial connect. On a transient failure, hand off to the auto-reconnect
        // backoff machine (modal). An auth failure already dropped us to login.
        connectToChat(authData)
          .then((result) => {
            // Mark the initial post-auth connect as DONE unconditionally —
            // BEFORE the cancelled check. This effect re-runs (and thus
            // "cancels") on every isConnecting flip, so gating the flag behind
            // `cancelled` meant it never got set: the effect then stayed armed
            // all session and fired a SECOND connectToChat in parallel with the
            // reconnect machine during deploy reconnects — two live sockets for
            // the same user, and the server displaced the tab's own connection
            // ("User connected from another location" loop).
            setHasConnectedAfterAuth(true)
            if (cancelled) return
            if (result && !result.ok && !result.authError) {
              startReconnect()
            }
          })
          .catch((e) => {
            console.error(e)
            setHasConnectedAfterAuth(true)
          })

        // IMPORTANT: StrictMode runs this cleanup between the two mounts.
        // If the effect is torn down, drop any connection that was made.
        return () => {
          cancelled = true
          if (cancelled && socketService.isConnected) {
            socketService.offAll()
            socketService.disconnect()
          }
        }
      }

      if (!authData) {
        console.log("🔄 No auth data found, requiring re-authentication...")
        setIsAuthenticated(false)
        setUser(null)
        setConnectionError("Session expired. Please log in again.")
      }
    }
  }, [
    isAuthenticated,
    tenantInfo,
    user,
    authData,
    connectToChat,
    isConnecting,
    idleKicked,
    hasConnectedAfterAuth,
    startReconnect,
  ])

  // Register the unexpected-disconnect hook: when an ESTABLISHED socket drops on
  // its own (network blip, server restart), kick off the auto-reconnect backoff.
  // Keeps messages + chat shell intact; the modal overlays and blocks input.
  useEffect(() => {
    socketService.onUnexpectedDisconnect = (reason, opts = {}) => {
      const maintenance = Boolean(opts.maintenance)
      console.log(
        `🔌 Unexpected disconnect (${maintenance ? "maintenance" : "network"}) — reconnecting:`,
        reason
      )
      setConnectionError(maintenance ? "Server updating" : "Connection lost")
      startReconnect(maintenance)
    }
    // Server announced a restart (deploy) BEFORE the socket dropped. Switch the
    // UI to maintenance messaging immediately so the user sees "updating, back
    // in a moment" rather than a flash of "connection lost" when the drop lands.
    socketService.onServerRestarting = () => {
      setIsMaintenance(true)
      setConnectionError("Server updating")
    }
    return () => {
      socketService.onUnexpectedDisconnect = null
      socketService.onServerRestarting = null
    }
  }, [startReconnect])

  // Mirror the outbound-message queue length into React state so the composer
  // can show how many messages are buffered while the socket is down. They
  // flush automatically on reconnect (see socketService._flushPending).
  useEffect(() => {
    socketService.onPendingChange = (count) => setPendingMessageCount(count)
    // Seed from current value in case messages were queued before this ran.
    setPendingMessageCount(socketService.pendingCount)
    return () => {
      socketService.onPendingChange = null
    }
  }, [])

  // Cancel any pending reconnect timer on unmount.
  useEffect(() => {
    return () => {
      reconnectingRef.current = false
      clearTimeout(reconnectTimerRef.current)
    }
  }, [])

  // Cleanup socket on unmount and page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("handleBeforeUnload()")
      if (socketService.isConnected) {
        socketService.disconnect()
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
      mediaService.reset()
      voiceChain.dispose() // releases the raw mic the chain holds
      remoteAudio.disposeAll()
      // Cancel any pending typing-stop timer so it can't fire after teardown.
      if (typingRef.current.stopTimer) {
        clearTimeout(typingRef.current.stopTimer)
        typingRef.current.stopTimer = null
      }
      if (socketService.isConnected) {
        socketService.offAll()
        socketService.disconnect()
      }
    }
  }, [])

  const handleLogout = async () => {
    // Tear down all WebRTC media (stop camera, close transports) before/around
    // the socket teardown regardless of outcome.
    mediaService.reset()
    voiceChain.dispose() // releases the raw mic the chain holds
    const clearLocalState = () => {
      authStorage.clear()
      pendingWatchRef.current.clear()
      setUser(null)
      setAuthData(null)
      setIsAuthenticated(false)
      setMessages([])
      setOnlineUsers([])
      setConnectionError(null)
      setHasConnectedAfterAuth(false)
      setProducersByUser({})
      setConsumedStreams({})
      setWatchingUsers([])
      setWatchedByUsers([])
      setBlockedWatchers([])
      setBlockedByUsers([])
      setSelfStream(null)
      setIsPublishing(false)
      publishIntentRef.current = null
      setSesh(null) // clear any sesh banner on logout
      setSeshSynced(false)
      resetImState()
    }
    try {
      // Disconnect from socket and wait for completion
      if (socketService.isConnected) {
        socketService.offAll()
        await socketService.disconnect()
      }
      clearLocalState()
    } catch (error) {
      console.error("Logout error:", error)
      // Force cleanup even if disconnect fails
      clearLocalState()
    }
  }

  const handleSendMessage = (message) => {
    try {
      if (!message || message.trim().length === 0) {
        console.warn("Empty message, not sending")
        return
      }

      // sendMessage queues the text when the socket is down (instead of
      // dropping it) and flushes it on reconnect. The pending count is mirrored
      // into UI via the onPendingChange hook below, so we don't gate on
      // isConnected here — a blip mid-sentence must not eat the message.
      console.log("📤 Sending message:", message)
      socketService.sendMessage(message, "TEXT")
      // Sending is itself an activity signal (the server stamps it). Close the
      // typing window so we don't leave a dangling typing-stop timer running.
      endTypingWindow()
    } catch (error) {
      console.error("❌ Failed to send message:", error)
      setConnectionError("Failed to send message")
    }
  }

  // Upload an image file to the backend, then send it as an IMAGE message whose
  // content is the returned (server-hosted) URL. The actual <input type=file>
  // lives in ChatPanel; this just handles the upload + send + error surfacing.
  // Resolves to true on success / false on failure so ChatPanel can clear its
  // pending state.
  const handleSendImage = async (file) => {
    if (!file) return false
    // Catch-all guest gate. The composer hides the attach button, the drop
    // overlay and the paste handler, but every image path — including the
    // drawing canvas, which builds a File directly — funnels through here, so
    // this is the one place guaranteed to cover all of them. The server would
    // 403 anyway; this just turns that into a readable message.
    if (!canUploadImages) {
      setConnectionError("Guests cannot upload images in this chat.")
      return false
    }
    if (!socketService.isConnected) {
      console.warn("Socket not connected, cannot send image")
      return false
    }
    if (!tenantInfo) {
      console.warn("No tenant info, cannot upload image")
      return false
    }
    try {
      console.log("🖼️ Uploading image:", file.name, file.type, file.size)
      const { url } = await uploadAPI.uploadImage(
        tenantInfo.tenantId,
        tenantInfo.scriptHash,
        file
      )
      socketService.sendMessage(url, "IMAGE")
      endTypingWindow()
      return true
    } catch (error) {
      console.error("❌ Failed to send image:", error)
      setConnectionError(error.message || "Failed to send image")
      return false
    }
  }

  // Upload a chosen image as the user's avatar, then persist it on their account
  // via the socket. The server acks with 'avatar-updated' and broadcasts
  // 'user-avatar-updated' to the room; both flow through applyAvatarUpdate, which
  // patches the header, sidebar, and the user's past messages live (no reload).
  // Resolves true/false so the caller (ProfileDialog) can clear its busy state.
  const handleAvatarChange = async (file) => {
    if (!file) return false
    if (!socketService.isConnected) {
      console.warn("Socket not connected, cannot change avatar")
      setConnectionError("Not connected — try again in a moment")
      return false
    }
    if (!tenantInfo) {
      console.warn("No tenant info, cannot upload avatar")
      return false
    }
    try {
      console.log("🖼️ Uploading avatar:", file.name, file.type, file.size)
      const { url } = await uploadAPI.uploadImage(
        tenantInfo.tenantId,
        tenantInfo.scriptHash,
        file
      )
      socketService.setAvatar(url)
      return true
    } catch (error) {
      console.error("❌ Failed to change avatar:", error)
      setConnectionError(error.message || "Failed to change avatar")
      return false
    }
  }

  // Persist a builtin tintable avatar ("builtin:NN:hex" — the recovered chat71
  // avatar set, see services/builtinAvatars.js). No upload involved: the spec
  // string rides the same set-avatar → avatar-updated/user-avatar-updated flow
  // as uploaded photos, and render sites expand it via resolveAvatarUrl.
  const handleAvatarPick = (spec) => {
    if (!spec) return false
    if (!socketService.isConnected) {
      console.warn("Socket not connected, cannot change avatar")
      setConnectionError("Not connected — try again in a moment")
      return false
    }
    socketService.setAvatar(spec)
    return true
  }

  // Tear down the current typing window: cancel the trailing timer and, if we
  // had announced typing, tell the server we stopped.
  const endTypingWindow = () => {
    const t = typingRef.current
    if (t.stopTimer) {
      clearTimeout(t.stopTimer)
      t.stopTimer = null
    }
    if (t.active) {
      t.active = false
      socketService.stopTyping()
    }
  }

  // Called on every keystroke in the chat input. Emits typing-start once on the
  // leading edge of a typing burst (cheap — one socket event, not one per key),
  // and (re)arms a trailing timer that emits typing-stop only after the user
  // has paused for TYPING_STOP_DELAY. The trailing delay is deliberately long
  // so the "active" presence signal doesn't flicker off between words —
  // matching the requested "bit of a bounce, don't stop too soon" behavior.
  const TYPING_STOP_DELAY = 3000
  const handleTyping = (text) => {
    if (!socketService.isConnected) return
    const t = typingRef.current

    // Leading edge: announce typing once per burst. An empty field doesn't
    // count as typing (e.g. the user cleared the input).
    if (text && text.trim().length > 0 && !t.active) {
      t.active = true
      socketService.startTyping()
    }

    // Reset the trailing stop timer on each keystroke ("don't stop too soon").
    if (t.stopTimer) clearTimeout(t.stopTimer)
    t.stopTimer = setTimeout(() => {
      t.stopTimer = null
      if (t.active) {
        t.active = false
        socketService.stopTyping()
      }
    }, TYPING_STOP_DELAY)
  }

  const handleMenuClick = () => {
    console.log("Menu clicked")
    // TODO: Implement menu functionality
  }

  // Append a local-only system notice to the chat stream (not sent to the
  // server). Used for requester-side webcam status: requested / watching /
  // denied. Rendered by ChatPanel's SYSTEM message branch.
  const addLocalSystemMessage = (content) => {
    setMessages((prev) =>
      [...prev, { id: `local_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, type: "SYSTEM", content }].slice(-MAX_MESSAGES)
    )
  }

  // --- IM mode (AIM-style private chat) -----------------------------------

  // Toggle IM mode on/off and persist it. Turning it OFF closes all open IM
  // windows (the conversations stay buffered, but private messages now land as
  // inline notices). Turning it ON re-opens windows for any conversations that
  // already have history.
  const handleImModeChange = useCallback((on) => {
    setImMode(!!on)
    try {
      localStorage.setItem(IM_MODE_STORAGE_KEY, on ? "on" : "off")
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
  }, [])

  // Toggle the retro AIM skin and persist it. Any open IM windows repaint live:
  // imIdentityRef feeds the new `classic` flag to their content on the next
  // sync, and the sync effect mirrors it into each window's `skin` option so the
  // chrome (frame + title bar) switches too.
  const handleImClassicChange = useCallback((on) => {
    setImClassic(!!on)
    try {
      localStorage.setItem(IM_CLASSIC_STORAGE_KEY, on ? "on" : "off")
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
  }, [])

  // Toggle the AIM message sounds (from the classic window's Edit menu) + persist.
  const handleImSoundsChange = useCallback((on) => {
    setImSounds(!!on)
    try {
      localStorage.setItem(IM_SOUNDS_STORAGE_KEY, on ? "on" : "off")
    } catch {
      /* ignore storage failures (private mode / quota) */
    }
  }, [])

  // Set / clear my away message. Relayed to the server, which auto-replies on my
  // behalf to anyone who IMs me while away. '' clears it.
  const handleAwayChange = useCallback((message) => {
    const text = (message || "").slice(0, 500)
    setAwayMessage(text)
    if (text.trim()) socketService.setAway(text.trim())
    else socketService.clearAway()
  }, [])

  // Send a private message to a partner. The optimistic add comes via the
  // server's 'private-sent' echo (ingested as mine) — same as room messages, so
  // we don't double up here.
  const handleImSend = useCallback((partnerId, text) => {
    const content = (text || "").trim()
    if (!content || !partnerId) return
    try {
      socketService.sendPrivateMessage(content, partnerId)
    } catch (err) {
      addLocalSystemMessage("Couldn't send that private message — you may be offline.")
    }
  }, [])

  // Relay my typing state in a specific IM window to that partner.
  const handleImTyping = useCallback((partnerId, isTyping) => {
    socketService.sendPrivateTyping(partnerId, isTyping)
  }, [])

  // Called (via ImFocusBridge) when the focused window changes — clears the
  // unread/blink flag on the now-focused conversation.
  const handleImWindowFocused = useCallback((focusedWid) => {
    if (!focusedWid || !focusedWid.startsWith("im:")) return
    const pid = focusedWid.slice(3)
    setImConversations((prev) =>
      prev[pid] && prev[pid].unread
        ? { ...prev, [pid]: { ...prev[pid], unread: false } }
        : prev
    )
  }, [])

  // Live refs for the values the IM window's render factory needs. The factory
  // is created ONCE when a window opens, so it must read these through refs to
  // avoid capturing stale identity/display prefs.
  const imSendRef = useRef(handleImSend)
  imSendRef.current = handleImSend
  const imTypingRef = useRef(handleImTyping)
  imTypingRef.current = handleImTyping
  const imIdentityRef = useRef({})
  imIdentityRef.current = {
    myUsername: authData?.membership?.username || user?.username || "You",
    myColor: usernameColor,
    fontSize,
    timestampFormat,
    classic: imClassic,
    soundsOn: imSounds,
    onToggleSounds: handleImSoundsChange,
  }
  // Standalone live ref to the classic flag too, so ensureImWindow (created once
  // per identity, not per-toggle) can seed a new window's `skin` option without
  // a stale read.
  const imClassicRef = useRef(imClassic)
  imClassicRef.current = imClassic
  // Live refs for the message-send/receive sound gating (read in the once-
  // registered socket ingest path, so must not capture stale state).
  const imSoundsRef = useRef(imSounds)
  imSoundsRef.current = imSounds

  // Ensure an IM window exists for a partner, opening it (via the window
  // manager) if not. The window's CONTENT is an <ImConversation> whose live data
  // comes from imConversationsRef (kept fresh by the sync effect below). Returns
  // the window id. Plays the "door open" swing on first open.
  const ensureImWindow = useCallback(
    (partner) => {
      if (!partner || !partner.id || partner.id === user?.id) return null
      const wid = `im:${partner.id}`
      if (wm.hasWindow(wid)) return wid
      playImSound("open", mentionVolumeRef.current)
      // Title: "[username] - Instant Message" (username keeps its color).
      const title = (
        <span>
          <span style={{ color: partner.usernameColor }}>{partner.username || "user"}</span>
          <span style={{ color: 'white', fontWeight: 600 }}>
            {" "}- Instant Message
          </span>
        </span>
      )
      wm.openWindow(
        () => {
          const conv = imConversationsRef.current[partner.id]
          const id = imIdentityRef.current
          return (
            <ImConversation
              partner={conv?.partner || partner}
              messages={conv?.messages || []}
              typing={!!conv?.typing}
              myUsername={id.myUsername}
              myColor={id.myColor}
              fontSize={id.fontSize}
              timestampFormat={id.timestampFormat}
              classic={id.classic}
              soundsOn={id.soundsOn}
              onToggleSounds={id.onToggleSounds}
              onSend={(t) => imSendRef.current(partner.id, t)}
              onTyping={(b) => imTypingRef.current(partner.id, b)}
            />
          )
        },
        {
          id: wid,
          title,
          width: 320,
          height: 380,
          minWidth: 260,
          minHeight: 240,
          // Retro chrome on/off — the manager forwards this opaquely to the
          // Window view, which swaps the frame + title bar. Kept in sync below.
          skin: imClassicRef.current ? "classic" : undefined,
          // Maximize covers just the chat message column (this element), not the
          // whole viewport. Falls back to viewport-minus-TopBar if absent.
          maximizeTarget: "#im-maximize-bounds",
        }
      )
      imWindowIdsRef.current[partner.id] = wid
      return wid
    },
    [wm, user?.id, theme]
  )

  // Open (or focus) the IM window for a partner from a user action (row menu).
  // Seeds an empty conversation if none exists, opens the window, and focuses it.
  const openImWindow = useCallback(
    (partner) => {
      if (!partner || !partner.id || partner.id === user?.id) return
      setImConversations((prev) =>
        prev[partner.id]
          ? prev
          : { ...prev, [partner.id]: { partner, messages: [], typing: false, unread: false } }
      )
      const wid = ensureImWindow(partner)
      if (wid) wm.focusWindow(wid)
    },
    [ensureImWindow, wm, user?.id]
  )

  // Ingest a private message (incoming OR my own sent-confirmation) into the
  // right conversation buffer. `mine` distinguishes my lines. An incoming
  // message ALWAYS opens/raises a window (that's the instant-messenger point)
  // and dings while the window isn't focused; the blink is driven by the
  // conversation's `unread` flag → the window's `attention` option (sync effect).
  const ingestPrivateMessage = useCallback(
    (pm, mine) => {
      // The partner is whoever ISN'T me.
      const peer = mine ? pm.to : pm.from
      if (!peer || !peer.id) return
      // Ignore / Mute: an incoming private message from an ignored user opens no
      // window and plays no sound. My OWN sends (mine) always go through — I can
      // still message someone I've muted; muting only hides THEIR communication.
      if (!mine && ignoredUserIdsRef.current.has(peer.id)) return
      const line = {
        id: pm.id || `pm_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        mine: !!mine,
        content: pm.content || "",
        type: pm.type || "PRIVATE",
        away: !!pm.away,
        timestamp: pm.timestamp || new Date(),
      }

      // Make sure a window exists for this partner (incoming or my own send).
      const partner = {
        id: peer.id,
        username: peer.username,
        usernameColor: peer.usernameColor || peer.textColor,
        role: peer.role,
      }
      ensureImWindow(partner)
      const wid = `im:${peer.id}`
      // Is the partner's window the focused one right now? (Drives ding + unread.)
      const isFocused = wm.isFocused ? wm.isFocused(wid) : false

      setImConversations((prev) => {
        const existing = prev[peer.id] || {
          partner,
          messages: [],
          typing: false,
          unread: false,
        }
        return {
          ...prev,
          [peer.id]: {
            ...existing,
            partner: { ...existing.partner, username: peer.username || existing.partner.username },
            messages: [...existing.messages, line].slice(-IM_MAX_LINES),
            // A real inbound line means the partner is no longer "typing".
            typing: mine ? existing.typing : false,
            // My own line clears unread; an incoming line marks unread unless its
            // window is focused right now.
            unread: mine ? false : existing.unread || !isFocused,
          },
        }
      })

      // Sound. In the classic (AIM) skin, use the real AOL samples — Send on my
      // own line, Recv on an incoming one — gated by the imSounds toggle. In the
      // default skin, keep the synthesized blip (incoming + unfocused only).
      const vol = mentionVolumeRef.current
      if (imClassicRef.current) {
        if (imSoundsRef.current) playImSound(mine ? "imsend" : "imrecv", vol)
      } else if (!mine && !isFocused) {
        playImSound("message", vol)
      }
    },
    [ensureImWindow, wm]
  )

  // Sync conversation state → the corresponding IM windows. The window content
  // reads live data from imConversationsRef, but React only re-renders a window
  // when its manager-state identity changes — so we bump a `rev` prop on each
  // conversation change to force the content to re-render, and mirror `unread`
  // into the window's `attention` option (drives the title-bar + taskbar blink).
  // Cheap: updateWindow no-ops for any partner without an open window.
  // Also re-runs when imClassic flips so open windows repaint: the `rev` string
  // folds in the classic flag (forcing a content re-render with the new skin),
  // and the `skin` option is mirrored so the chrome (frame + title bar) swaps.
  useEffect(() => {
    // The `rev` folds in the classic + sounds flags so a toggle of either forces
    // the window content to re-render (e.g. the Edit-menu sound checkmark).
    const flagTag = (imClassic ? "c" : "") + (imSounds ? "s" : "")
    for (const pid in imConversations) {
      const wid = `im:${pid}`
      if (!wm.hasWindow(wid)) continue
      const conv = imConversations[pid]
      wm.updateWindow(
        wid,
        { rev: conv.messages.length + (conv.typing ? "t" : "") + flagTag },
        undefined,
        { attention: !!conv.unread, skin: imClassic ? "classic" : undefined }
      )
    }
  }, [imConversations, imClassic, imSounds, wm])

  // Apply a noodle-shame entry: drop the jester hat on `entry.userId` and arm a
  // timer to lift it at `entry.shameUntil` (the fumbled 420/350). Ignores a stale
  // entry whose boundary has already passed. A fresh noodle cancels the prior
  // timer (only the most recent noodler is shamed). Defined as a callback so the
  // once-registered socket listener (below) and the late-join ack both reuse it.
  const applyNoodleShame = useCallback((entry) => {
    if (!entry || !entry.userId || !entry.shameUntil) return
    const msLeft = entry.shameUntil - Date.now()
    if (msLeft <= 0) return // boundary already passed — nothing to show
    if (noodleShameTimerRef.current) clearTimeout(noodleShameTimerRef.current)
    setNoodleShame(entry)
    noodleShameTimerRef.current = setTimeout(() => {
      // Only clear if it's still THIS shame (a newer noodle may have replaced it,
      // which would have armed its own timer — don't yank the new hat).
      setNoodleShame((prev) => (prev && prev.shameUntil === entry.shameUntil ? null : prev))
      setNoodleTrack(null) // drop any live tracked-hat transform too
      noodleShameTimerRef.current = null
    }, msLeft)
    // A fresh noodler invalidates the previous noodler's tracked transform.
    setNoodleTrack(null)
  }, [])

  // Clear any pending shame timer on unmount so it can't fire into a dead tree.
  useEffect(() => {
    return () => {
      if (noodleShameTimerRef.current) clearTimeout(noodleShameTimerRef.current)
      if (headTrackStopRef.current) { headTrackStopRef.current(); headTrackStopRef.current = null }
    }
  }, [])

  // NOODLER-SIDE HEAD TRACKING. When WE are the currently-shamed noodler AND our
  // camera is publishing with video on, lazily spin up MediaPipe on our OWN local
  // stream and stream the head transform to the room so watchers pin the hat to
  // our head. Gated on device capability + bandwidth (skips the ~4MB download on
  // slow/metered links); if the device can't sustain the FPS floor the tracker
  // self-stops and everyone falls back to the static hat. We track an OFFSCREEN
  // <video> bound to the existing selfStream — never a new getUserMedia — so iOS's
  // single-camera limit isn't tripped (the camera is already live for publishing).
  const amNoodler = !!noodleShame && !!user && noodleShame.userId === user.id
  const canTrackNow = amNoodler && isPublishing && videoTrackOn && !!selfStream
  useEffect(() => {
    let stopped = false
    let offscreenVideo = null
    let lastSent = 0

    const teardown = () => {
      if (headTrackStopRef.current) { headTrackStopRef.current(); headTrackStopRef.current = null }
      if (offscreenVideo) {
        offscreenVideo.srcObject = null
        offscreenVideo = null
      }
      // Tell watchers to drop the tracked hat (fall back to static) when we stop —
      // and drop our own local echo too.
      socketService.sendNoodleTrack({ gone: true })
      setNoodleTrack(null)
    }

    if (!canTrackNow) return undefined

    const myUserId = user.id

    ;(async () => {
      try {
        const mod = await import("./services/headTracker")
        if (stopped) return
        if (!mod.canProbablyTrack() || !mod.bandwidthOkForDownload()) return // fall back silently

        // Offscreen <video> reading the ALREADY-LIVE self stream (no new camera).
        offscreenVideo = document.createElement("video")
        offscreenVideo.muted = true
        offscreenVideo.playsInline = true
        offscreenVideo.srcObject = selfStream
        await offscreenVideo.play().catch(() => {})
        if (stopped) { offscreenVideo.srcObject = null; return }

        const stop = await mod.startHeadTracking(offscreenVideo, {
          onTransform: (t) => {
            // LOCAL ECHO first: the server relay (socket.to) excludes the sender,
            // so we'd never see our own hat otherwise. Apply the transform to our
            // own state directly — full frame rate, no network round-trip — so the
            // noodler's self-tile tracks too (and even smoother than watchers').
            setNoodleTrack(t ? { userId: myUserId, ...t } : null)
            const now = performance.now()
            if (!t) { socketService.sendNoodleTrack({ gone: true }); return }
            // Throttle sends to ~18/s (the smoothing + CSS transition keep it fluid).
            if (now - lastSent < 55) return
            lastSent = now
            socketService.sendNoodleTrack(t)
          },
          onFallback: (reason) => {
            console.log("🍜 head-track fallback:", reason)
            socketService.sendNoodleTrack({ gone: true })
            setNoodleTrack(null)
          },
        })
        if (stopped) { stop(); return }
        headTrackStopRef.current = stop
      } catch (err) {
        console.warn("head-track init failed:", err.message)
      }
    })()

    return () => { stopped = true; teardown() }
  }, [canTrackNow, selfStream, user])

  // --- Group "Sesh" Countdown actions ---
  // Start a sesh (members only). The SERVER enforces the member/guest gate and
  // the one-per-room rule; we surface a rejection (e.g. guest, or already
  // running) as a local notice. On success the server broadcasts sesh:start
  // (incl. to us), so we don't optimistically set state here — we render what
  // the server sends, keeping every client in lockstep.
  const handleStartSesh = () => {
    if (!socketService.isConnected) return
    socketService.startSesh().catch((err) => {
      console.warn("Could not start sesh:", err.message)
      addLocalSystemMessage(err.message || "Could not start a sesh right now.")
    })
  }

  // Join the live sesh (any user, JOIN window only). Server dedupes + phase-gates
  // and broadcasts the updated smoker list; a rejection (too late) is surfaced
  // locally. No optimistic add — the sesh:smokers broadcast is authoritative.
  const handleJoinSesh = () => {
    if (!socketService.isConnected) return
    socketService.joinSesh().catch((err) => {
      console.warn("Could not join sesh:", err.message)
      addLocalSystemMessage(err.message || "Could not join the sesh.")
    })
  }

  // Two self-views, two sources:
  //  - the inline sidebar self-MONITOR uses the LOCAL camera stream (selfStream).
  //  - WATCHING yourself (this handler, the grid "You" tile) consumes your OWN
  //    producer through the SFU, so you see your post-encode output exactly as
  //    other users receive it — same path as watching anyone else.
  // Watching another user is a two-step handshake (mirrors the competitor's
  // webcamRequest): clicking the camera icon SENDS a webcam request; once they
  // accept (instantly when public, after manual approval when private), the
  // rtc:webcam-accept handler performs the real consume.
  const handleWatchUser = async (userId) => {
    const isSelf = userId === user?.id

    if (isSelf) {
      if (!mediaService.isPublishing()) {
        console.warn("Enable your webcam before watching yourself")
        return
      }
      setWatchingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]))
      // The eye reads watchedByUsers; record self as a watcher of own cam.
      setWatchedByUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]))
      try {
        // Post-encode self view via the SFU (no webcamRequest handshake needed
        // for your own producer).
        const stream = await mediaService.consumeSelf("default")
        if (stream) setConsumedStreams((prev) => ({ ...prev, [userId]: stream }))
        console.log("👁️ Watching your own cam (SFU post-encode)")
      } catch (err) {
        console.error("❌ Failed to consume own cam:", err.message)
        setWatchingUsers((prev) => prev.filter((id) => id !== userId))
        setWatchedByUsers((prev) => prev.filter((id) => id !== userId))
      }
      return
    }

    const producerId = producersByUser[userId]
    if (!producerId) {
      console.warn("Cannot watch user with no active webcam:", userId)
      return
    }
    // Anti-spam: refuse to re-request a publisher who just denied me, until the
    // cooldown clears (the sidebar also dims/disables their cam icon).
    if (deniedCooldownUsers.includes(userId)) {
      console.log("⏳ Cam request on cooldown for user:", userId)
      return
    }
    // Synchronous in-flight guard: a rapid double-click would otherwise emit two
    // requests (the watchingUsers state check races across closures). The ref is
    // checked-and-set atomically so only the first click sends a request.
    if (pendingWatchRef.current.has(userId)) return
    pendingWatchRef.current.add(userId)

    // Do NOT optimistically add a tile. The publisher may be in private mode and
    // deny (or ignore) the request — we don't want a stuck "connecting…" tile.
    // The tile appears only when rtc:webcam-accept arrives and we add to
    // watchingUsers + attach the stream (see onWebcamAccept in setupSocketListeners).
    // No notice is printed here either: whether this is a gated request or an
    // instant auto-accepted watch is the PUBLISHER's state, which we can't see.
    // The truthful signal comes back as either rtc:webcam-queued ("You
    // requested…", private) or rtc:webcam-accept ("You are now watching…").
    try {
      console.log("📨 Requesting webcam of user:", userId)
      await socketService.requestWebcam(userId)
    } catch (err) {
      console.error("❌ Webcam request failed:", err.message)
      pendingWatchRef.current.delete(userId)
    }
  }

  const handleUnwatchUser = (userId) => {
    const isSelf = userId === user?.id
    pendingWatchRef.current.delete(userId)
    setWatchingUsers((prev) => prev.filter((id) => id !== userId))
    // The tile is going away — close its stats panel too so it doesn't linger,
    // and forget its dragged position so a future reopen anchors to the tile.
    handleCloseStats(userId)
    delete statsPosRef.current[userId]
    delete statsAnchorsRef.current[userId]

    if (isSelf) {
      // Stop watching myself: close the SFU self-consumer + drop the grid tile
      // and my own watcher eye. The inline sidebar monitor (local stream) is
      // unaffected — it lives until the cam is disabled.
      const ownProducerId = mediaService.getOwnProducerId()
      if (ownProducerId) mediaService.stopConsuming(ownProducerId)
      setConsumedStreams((prev) => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      setWatchedByUsers((prev) => prev.filter((id) => id !== userId))
      console.log("👁️ Stopped watching your own cam (grid tile)")
      return
    }

    const producerId = producersByUser[userId]
    // notifyServer: tell the SFU to close the consumer + turn off the
    // publisher's "watched-by" eye (the user explicitly unwatched).
    if (producerId) mediaService.stopConsuming(producerId, true)
    setConsumedStreams((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
    remoteAudio.detach(userId)
    console.log("👁️ Stopped watching user:", userId)
  }

  // Private-mode approval: I accept a queued request -> tell the requester
  // (rtc:webcam-accept) so they consume my producer, then drop the banner entry.
  const handleAcceptCamRequest = (fromUserId) => {
    socketService.acceptWebcam(fromUserId).catch((err) =>
      console.error("❌ Failed to accept webcam request:", err.message)
    )
    setPendingCamRequests((prev) => prev.filter((r) => r.fromUserId !== fromUserId))
  }

  // Private-mode approval: I deny a queued request. Tell the requester so they
  // see a denial notice and clear their in-flight guard; then drop the banner.
  const handleDenyCamRequest = (fromUserId) => {
    console.log("🚫 Denied webcam request from:", fromUserId)
    socketService.denyWebcam(fromUserId).catch((err) =>
      console.error("❌ Failed to send webcam deny:", err.message)
    )
    setPendingCamRequests((prev) => prev.filter((r) => r.fromUserId !== fromUserId))
  }

  // INVITE someone to view MY cam (the "Invite to Private Chat" context-menu
  // item). Only meaningful while I'm publishing; record a one-shot pre-acceptance
  // token so the request their acceptance fires is auto-accepted, then poke them.
  const handleInviteUser = (targetUserId) => {
    if (!targetUserId || targetUserId === user?.id) return
    if (!isPublishing) {
      console.warn("Cannot invite to view cam while not publishing")
      return
    }
    const targetName = onlineUsers.find((u) => u.id === targetUserId)?.username || "user"
    outstandingInvitesRef.current.add(targetUserId)
    socketService.inviteWebcam(targetUserId).then(() => {
      addLocalSystemMessage(`You invited ${targetName} to view your webcam.`)
    }).catch((err) => {
      console.error("❌ Failed to send cam invite:", err.message)
      outstandingInvitesRef.current.delete(targetUserId)
    })
  }

  // Invitee side — I ACCEPT a cam invite. Fire a viaInvite watch request back to
  // the inviter; their client auto-accepts it (rtc:webcam-accept), which my
  // existing onWebcamAccept handler turns into a consume. Going through the
  // normal request path means a blocked/gone publisher still fails gracefully.
  const handleAcceptCamInvite = (fromUserId) => {
    setPendingCamInvites((prev) => prev.filter((r) => r.fromUserId !== fromUserId))
    if (pendingWatchRef.current.has(fromUserId)) return
    pendingWatchRef.current.add(fromUserId)
    const targetName = onlineUsers.find((u) => u.id === fromUserId)?.username || "user"
    socketService.requestWebcam(fromUserId, { viaInvite: true }).then(() => {
      addLocalSystemMessage(`Accepted ${targetName}'s invite — opening their webcam…`)
    }).catch((err) => {
      console.error("❌ Failed to accept cam invite:", err.message)
      pendingWatchRef.current.delete(fromUserId)
    })
  }

  // Invitee side — I DENY a cam invite. Tell the inviter (clears their token +
  // shows a notice) and drop the banner.
  const handleDenyCamInvite = (fromUserId) => {
    socketService.denyWebcamInvite(fromUserId).catch((err) =>
      console.error("❌ Failed to deny cam invite:", err.message)
    )
    setPendingCamInvites((prev) => prev.filter((r) => r.fromUserId !== fromUserId))
  }

  // Invitee side — I IGNORE a cam invite: silent local dismiss, no event sent.
  // The inviter's pre-acceptance token simply sits unused (harmless — it's only
  // ever consumed by a matching viaInvite request, which I never send).
  const handleIgnoreCamInvite = (fromUserId) => {
    setPendingCamInvites((prev) => prev.filter((r) => r.fromUserId !== fromUserId))
  }

  // Stub handler for the not-yet-built user-row context-menu actions (personal
  // message, whisper, send file, profile, friends, mute, kick/ban/etc). Surfaces
  // a "coming soon" notice so the menu items are visible + clickable now and can
  // be wired up one at a time later. The wired items (Invite/Show Cam/Stop/
  // Prohibit) do NOT route through here.
  const handleRowMenuAction = (action, rowUserId) => {
    // Two-sided guest gates. The menu already hides these items, so reaching
    // here means a stale menu or a hand-fired action — refuse quietly rather
    // than opening a window the server will refuse to carry.
    const rowRole = onlineUsers.find((u) => u.id === rowUserId)?.role
    // Wired action: send a file directly to this user (peer-to-peer offer).
    if (action === "sendFile") {
      if (!canUseCapWith(guestFeatures, isGuest, rowRole, "files")) return
      handleStartSendFile(rowUserId)
      return
    }
    // Wired action: open an AIM-style IM window with this user. Explicitly
    // opening one turns IM mode ON so the window actually appears.
    if (action === "privateChat") {
      if (rowUserId === user?.id) return // can't IM yourself
      if (!canUseCapWith(guestFeatures, isGuest, rowRole, "privateChat")) return
      const row = onlineUsers.find((u) => u.id === rowUserId)
      if (!imMode) handleImModeChange(true)
      openImWindow({
        id: rowUserId,
        username: row?.username || "user",
        usernameColor: row?.usernameColor || row?.textColor,
        role: row?.role,
      })
      return
    }
    // Wired action: open a read-only ProfileCard for this user (identity + their
    // usage stats). Pulls the row's display fields from onlineUsers.
    if (action === "profile") {
      const row = onlineUsers.find((u) => u.id === rowUserId)
      if (row) setProfileCardUser(row)
      return
    }
    // Wired admin action: kick this user from the room. The server re-checks that
    // we're an ADMIN/OWNER; it notifies the target and disconnects them.
    if (action === "kick") {
      if (rowUserId === user?.id) return // can't kick yourself
      const kickedName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      socketService
        .kickUser(rowUserId)
        .then(() => addLocalSystemMessage(`You kicked ${kickedName}.`))
        .catch((err) => {
          console.error("❌ Kick failed:", err.message)
          addLocalSystemMessage(`Couldn't kick ${kickedName}: ${err.message}`)
        })
      return
    }
    // --- Friends v1: four-state menu actions. Each updates the local Sets
    // optimistically and reverts + reports on a server rejection (kick error
    // style). The friend graph is otherwise kept live by the two subscription
    // events + the post-join friend:list. ---
    if (action === "friendRequest") {
      if (rowUserId === user?.id) return
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      setOutgoingRequestIds((prev) => new Set(prev).add(rowUserId))
      socketService
        .requestFriend(rowUserId)
        .then((ack) => {
          // Cross-request auto-accept: the server accepted instead of queuing —
          // move them straight into friends.
          if (ack && ack.autoAccepted) {
            setOutgoingRequestIds((prev) => {
              const next = new Set(prev)
              next.delete(rowUserId)
              return next
            })
            setIncomingRequestIds((prev) => {
              const next = new Set(prev)
              next.delete(rowUserId)
              return next
            })
            setFriendIds((prev) => new Set(prev).add(rowUserId))
            addLocalSystemMessage(`You and ${targetName} are now friends.`)
          }
        })
        .catch((err) => {
          setOutgoingRequestIds((prev) => {
            const next = new Set(prev)
            next.delete(rowUserId)
            return next
          })
          addLocalSystemMessage(`Couldn't add ${targetName}: ${err.message}`)
        })
      return
    }
    if (action === "friendCancel") {
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      setOutgoingRequestIds((prev) => {
        const next = new Set(prev)
        next.delete(rowUserId)
        return next
      })
      socketService.cancelFriendRequest(rowUserId).catch((err) => {
        setOutgoingRequestIds((prev) => new Set(prev).add(rowUserId))
        addLocalSystemMessage(`Couldn't cancel request to ${targetName}: ${err.message}`)
      })
      return
    }
    if (action === "friendAccept" || action === "friendDecline") {
      const accept = action === "friendAccept"
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      setIncomingRequestIds((prev) => {
        const next = new Set(prev)
        next.delete(rowUserId)
        return next
      })
      if (accept) setFriendIds((prev) => new Set(prev).add(rowUserId))
      socketService
        .respondFriend(rowUserId, accept)
        .then(() => {
          if (accept) addLocalSystemMessage(`You and ${targetName} are now friends.`)
        })
        .catch((err) => {
          // Revert: put the pending request back, drop the optimistic friend.
          setIncomingRequestIds((prev) => new Set(prev).add(rowUserId))
          if (accept) {
            setFriendIds((prev) => {
              const next = new Set(prev)
              next.delete(rowUserId)
              return next
            })
          }
          addLocalSystemMessage(`Couldn't respond to ${targetName}: ${err.message}`)
        })
      return
    }
    if (action === "friendRemove") {
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      // No confirm-dialog component exists in App yet — window.confirm per spec.
      if (!window.confirm(`Remove ${targetName} from your friends?`)) return
      setFriendIds((prev) => {
        const next = new Set(prev)
        next.delete(rowUserId)
        return next
      })
      socketService.removeFriend(rowUserId).catch((err) => {
        setFriendIds((prev) => new Set(prev).add(rowUserId))
        addLocalSystemMessage(`Couldn't remove ${targetName}: ${err.message}`)
      })
      return
    }
    // --- Ignore / Mute v1: toggle against MY authoritative Set (optimistic,
    // reverted on server rejection). App owns the current state, so the menu
    // sends one toggle action rather than distinct ignore/unignore verbs. ---
    if (action === "ignoreToggle") {
      if (rowUserId === user?.id) return
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      const wasIgnored = ignoredUserIds.has(rowUserId)
      const nowIgnored = !wasIgnored
      setIgnoredUserIds((prev) => {
        const next = new Set(prev)
        if (nowIgnored) next.add(rowUserId)
        else next.delete(rowUserId)
        return next
      })
      addLocalSystemMessage(
        nowIgnored
          ? `You are now ignoring ${targetName}. Their messages are hidden from you.`
          : `You are no longer ignoring ${targetName}.`
      )
      socketService.setIgnore(rowUserId, nowIgnored).catch((err) => {
        // Revert to the prior state on rejection.
        setIgnoredUserIds((prev) => {
          const next = new Set(prev)
          if (wasIgnored) next.add(rowUserId)
          else next.delete(rowUserId)
          return next
        })
        addLocalSystemMessage(`Couldn't update ignore for ${targetName}: ${err.message}`)
      })
      return
    }
    // Wired admin action: permanently ban this user from the workspace. The
    // server sets Membership.isBanned so their socket reconnect and REST auth
    // are refused. No unban UI in v1 — tenant-admin follow-up.
    if (action === "ban") {
      if (rowUserId === user?.id) return
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      // window.confirm: no confirm-dialog component exists in App (Phase 3
      // introduced this pattern for Remove Friend — matching it here).
      if (!window.confirm(`Ban ${targetName} from this chat? They will be disconnected and cannot rejoin.`)) return
      socketService
        .banUser(rowUserId)
        .then(() => addLocalSystemMessage(`${targetName} has been banned.`))
        .catch((err) => {
          console.error("❌ Ban failed:", err.message)
          addLocalSystemMessage(`Couldn't ban ${targetName}: ${err.message}`)
        })
      return
    }
    // Wired admin action: ban the IP address of this user's current connection
    // AND ban their membership. Requires them to be live in the room. No unban
    // UI in v1 — rows deleted manually; tenant-admin follow-up.
    if (action === "banIp") {
      if (rowUserId === user?.id) return
      const targetName = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
      if (!window.confirm(`Ban ${targetName}'s IP address? This will disconnect them and block reconnects from that IP.`)) return
      socketService
        .banIpUser(rowUserId)
        .then((ack) => addLocalSystemMessage(`${targetName}'s IP has been banned${ack?.ip ? ` (${ack.ip})` : ""}.`))
        .catch((err) => {
          console.error("❌ Ban-IP failed:", err.message)
          addLocalSystemMessage(`Couldn't ban ${targetName}'s IP: ${err.message}`)
        })
      return
    }
    const name = onlineUsers.find((u) => u.id === rowUserId)?.username || "user"
    const labels = {
      privateChat: `Private chat with ${name}`,
      profile: `${name}'s profile`,
      kick: `Kick ${name}`,
    }
    console.log("🧩 Row action (stub):", action, rowUserId)
    addLocalSystemMessage(`${labels[action] || "That action"} — coming soon.`)
  }

  // Summon the shared user context menu at a screen point. Both surfaces
  // (sidebar rows, chat usernames) funnel through here so the menu is one
  // instance with user-scoped — not surface-scoped — content.
  const handleOpenUserMenu = (userId, position) => {
    if (!userId || !position) return
    setUserMenu({ userId, position })
  }

  // The open menu's enriched target row, re-derived each render from the live
  // onlineUsers list with the SAME enrichment helper RightSidebar's list uses
  // (isMe / hasWebcam / micOnly / presence — see enrichUser). If the target
  // logs off while the menu is open this resolves to null and the menu
  // dissolves rather than acting on a ghost.
  const userMenuRow = userMenu
    ? onlineUsers.find((u) => u.id === userMenu.userId)
    : null
  const userMenuUser = userMenuRow
    ? enrichUser(userMenuRow, {
        currentUserId: user?.id,
        isPublishing,
        producersByUser,
        pausedVideoUsers,
        videoTrackOn,
        now: Date.now(),
      })
    : null

  // --- Peer-to-peer file transfer handlers --------------------------------

  // Drop a transfer's progress entry from the UI.
  const clearFileTransfer = (transferId) => {
    setFileTransfers((prev) => {
      if (!prev[transferId]) return prev
      const next = { ...prev }
      delete next[transferId]
      return next
    })
  }

  // Strip any path components / control chars from a sender-supplied filename
  // before using it for a download. (Display in the banner is already safe —
  // React escapes text — but the download attribute should never carry a path.)
  const sanitizeDownloadName = (name) => {
    const base = String(name || "file").split(/[\\/]/).pop() || "file"
    // eslint-disable-next-line no-control-regex
    return base.replace(/[\x00-\x1f]/g, "").slice(0, 200) || "file"
  }

  // Assemble a completed incoming transfer into a Blob and trigger a download,
  // then tear down its buffer + progress entry.
  const saveReceivedFile = (transferId) => {
    const entry = incomingFilesRef.current.get(transferId)
    if (!entry) return
    incomingFilesRef.current.delete(transferId)
    try {
      const blob = new Blob(entry.chunks, {
        type: entry.meta.mime || "application/octet-stream",
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = sanitizeDownloadName(entry.meta.name)
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Revoke after a tick so the download has surely started.
      setTimeout(() => URL.revokeObjectURL(url), 10000)
      addLocalSystemMessage(
        `Received "${entry.meta.name}" from ${entry.meta.fromUsername || "a user"}.`
      )
    } catch (err) {
      console.error("❌ Failed to save received file:", err.message)
      addLocalSystemMessage(`Failed to save the received file.`)
    } finally {
      clearFileTransfer(transferId)
    }
  }

  // Sender side — open the file picker for a target user. The chosen file is
  // offered (metadata only) in handleFilePicked.
  const handleStartSendFile = (rowUserId) => {
    if (!rowUserId || rowUserId === user?.id) return // can't send to yourself
    // Two-sided guest gate, re-checked here so any future caller inherits it.
    // Spoken feedback rather than a silent return: reaching this means the
    // control was visible, so the user deserves to know why nothing happened.
    const rowRole = onlineUsers.find((u) => u.id === rowUserId)?.role
    if (!canUseCapWith(guestFeatures, isGuest, rowRole, "files")) {
      addLocalSystemMessage(
        isGuest
          ? "Guests can't send files in this chat."
          : "That user is a guest and can't receive files."
      )
      return
    }
    if (!socketService.isConnected) {
      addLocalSystemMessage("You're offline — can't send a file right now.")
      return
    }
    fileSendTargetRef.current = rowUserId
    if (fileSendInputRef.current) {
      fileSendInputRef.current.value = "" // allow re-picking the same file
      fileSendInputRef.current.click()
    }
  }

  // A file was chosen -> validate size, generate a transferId, hold the File,
  // and send the OFFER. Bytes wait until the recipient accepts (onFileAccept).
  const handleFilePicked = (e) => {
    const file = e.target.files && e.target.files[0]
    const targetUserId = fileSendTargetRef.current
    if (!file || !targetUserId) return
    const name = onlineUsers.find((u) => u.id === targetUserId)?.username || "user"
    if (file.size > FILE_TRANSFER_MAX_BYTES) {
      addLocalSystemMessage(
        `"${file.name}" is too large (max ${Math.round(FILE_TRANSFER_MAX_BYTES / (1024 * 1024))} MB).`
      )
      return
    }
    // Generate a unique-enough transfer id without external deps.
    const transferId = `ft_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
    outgoingFilesRef.current.set(transferId, file)
    socketService
      .offerFile(targetUserId, { transferId, name: file.name, size: file.size, mime: file.type })
      .then(() => {
        addLocalSystemMessage(`Offering "${file.name}" to ${name} — waiting for them to accept…`)
      })
      .catch((err) => {
        outgoingFilesRef.current.delete(transferId)
        addLocalSystemMessage(`Couldn't offer the file: ${err.message}`)
      })
  }

  // Receiver side — accept an incoming offer: set up the reassembly buffer and
  // a receive-progress entry, then tell the sender to start streaming.
  const handleAcceptFileOffer = (offer) => {
    incomingFilesRef.current.set(offer.transferId, {
      chunks: [],
      received: 0,
      meta: { name: offer.name, size: offer.size, mime: offer.mime, fromUsername: offer.fromUsername },
    })
    setFileTransfers((prev) => ({
      ...prev,
      [offer.transferId]: {
        dir: "recv",
        peerUsername: offer.fromUsername,
        name: offer.name,
        size: offer.size,
        sent: 0,
      },
    }))
    setPendingFileOffers((prev) => prev.filter((o) => o.transferId !== offer.transferId))
    socketService.acceptFile(offer.fromUserId, offer.transferId).catch((err) => {
      console.error("❌ Failed to accept file:", err.message)
      incomingFilesRef.current.delete(offer.transferId)
      clearFileTransfer(offer.transferId)
      addLocalSystemMessage("Couldn't accept the file.")
    })
  }

  // Receiver side — deny an incoming offer.
  const handleDenyFileOffer = (offer) => {
    setPendingFileOffers((prev) => prev.filter((o) => o.transferId !== offer.transferId))
    socketService
      .denyFile(offer.fromUserId, offer.transferId)
      .catch((err) => console.error("❌ Failed to deny file:", err.message))
  }

  // Toggle whether a row-user may watch MY cam (the eye toggle in RightSidebar).
  // Blocking ends their active session server-side and stops them re-watching;
  // the row's eye then renders slashed. Unblocking lets them watch again.
  const handleToggleWatchPermission = (rowUserId) => {
    if (!rowUserId || rowUserId === user?.id) return // never block yourself
    const isBlocked = blockedWatchers.includes(rowUserId)
    const allowed = isBlocked // toggling: if blocked -> allow, else block

    // Whether they were watching me at toggle time — captured up front so the
    // failure path can restore it (the server may refuse the block, e.g. when
    // the target outranks me, and their session was never actually torn down).
    const wasWatchingMe = watchedByUsers.includes(rowUserId)

    // Optimistic UI: flip the block set, and on block also clear their
    // "watching me" eye since their session is being torn down.
    setBlockedWatchers((prev) =>
      allowed ? prev.filter((id) => id !== rowUserId) : [...new Set([...prev, rowUserId])]
    )
    if (!allowed) {
      setWatchedByUsers((prev) => prev.filter((id) => id !== rowUserId))
    }

    socketService.setWatchPermission(rowUserId, allowed).catch((err) => {
      console.error("❌ Failed to toggle watch permission:", err.message)
      // Roll back the optimistic block-set change on failure.
      setBlockedWatchers((prev) =>
        allowed ? [...new Set([...prev, rowUserId])] : prev.filter((id) => id !== rowUserId)
      )
      if (!allowed && wasWatchingMe) {
        setWatchedByUsers((prev) => [...new Set([...prev, rowUserId])])
      }
      // Explain the refusal rather than letting the eye silently snap back. The
      // privilege floor (can't block a moderator) is the expected case here.
      addLocalSystemMessage(err.message || "Couldn't change that cam permission.")
    })
  }

  // Change the quality we receive for a watched user (capped at publisher max).
  const handleViewQualityChange = (userId, quality) => {
    const producerId = producersByUser[userId]
    if (producerId) mediaService.setViewQuality(producerId, quality)
  }

  // Acquire a getUserMedia stream for the chosen devices, with the full
  // stale-deviceId re-resolution + cam+mic / video-only / default-camera
  // fallback chain. Shared by the fresh-publish path (handleEnableWebcam) and the
  // hot-swap path (handleSwapDevices) so both behave identically. Returns
  // { stream, resolvedVideoId, resolvedVideoLabel }.
  const acquirePublishStream = async (opts = {}) => {
    const {
      videoLabel, audioLabel, videoOnly = false, prevAudioDeviceId,
      // Initial publish: if the pinned camera can't open, fall back to the
      // system default so the user still joins with *some* camera. An explicit
      // device SWITCH passes false — falling back to a different camera there
      // silently discards the user's choice (and overwrites their saved pin), so
      // we'd rather fail the swap loudly and keep the current feed. See
      // handleSwapDevices.
      allowDefaultFallback = true,
    } = opts
    let { videoDeviceId, audioDeviceId } = opts
    // Chrome's deviceId is a per-origin salted hash that can ROTATE between
    // sessions (storage/permission churn, virtual-camera reshuffles), so a
    // persisted raw deviceId may no longer match any enumerated device even
    // though the camera is still plugged in — an exact pin then rejects with
    // OverconstrainedError(deviceId) on every reload. Re-resolve the intended
    // device by its (stable) label against the CURRENT device list, and if it
    // moved, adopt the fresh id so the rest of this function pins a live id.
    const resolveDeviceId = async (id, label, kind) => {
      if (!label) return id // nothing to resolve against; trust the id
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const ofKind = devices.filter((d) => d.kind === kind)
        if (id && ofKind.some((d) => d.deviceId === id)) return id // still valid
        const byLabel = ofKind.find((d) => d.label && d.label === label)
        if (byLabel) {
          console.warn(
            `⚠️ ${kind}: saved deviceId stale, re-resolved "${label}" to current id`
          )
          return byLabel.deviceId
        }
      } catch (e) {
        console.warn("⚠️ device resolve failed:", e.message)
      }
      return id // give exact a chance; the retry chain handles a hard fail
    }
    videoDeviceId = await resolveDeviceId(videoDeviceId, videoLabel, "videoinput")
    audioDeviceId = await resolveDeviceId(audioDeviceId, audioLabel, "audioinput")

    // Capture cam + mic. If the mic is unavailable (no device / denied), fall
    // back to video-only so a missing mic never blocks the webcam — the
    // combined getUserMedia rejects entirely if either track can't be opened.
    let stream
    // Ask for the best the cam can give us: 1080p60. These are `ideal`/`max`
    // (never `exact`), so a webcam that can't hit 1080p60 negotiates down to
    // its best mode instead of getUserMedia rejecting outright.
    const baseVideo = {
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      frameRate: { ideal: 60, max: 60 }
    }
    // Build constraints, optionally pinning the chosen devices. A saved
    // deviceId can be stale (device unplugged) → exact rejects → we fall back
    // to the system default below.
    const video = videoDeviceId
      ? { ...baseVideo, deviceId: { exact: videoDeviceId } }
      : baseVideo
    // Explicit DSP constraints so capture quality is deterministic across
    // browsers (Chrome defaults these on, but Firefox/Safari vary).
    // AGC is OFF on purpose: when someone walks away, AGC ramps gain up and
    // surfaces background noise (TV, room) — disabling it keeps levels stable
    // at the cost of soft/distant talkers coming through quieter (the vocal
    // chain's input-gain slider is the manual replacement). EC/NS follow the
    // user's mic-processing prefs: studio-mic/headphone users can turn the
    // browser DSP off (changing these requires this re-acquire path).
    const micProc = micProcessingRef.current
    const baseAudio = {
      echoCancellation: !micProc.disableEchoCancellation,
      noiseSuppression: !micProc.disableNoiseSuppression,
      autoGainControl: false,
    }
    const audio = audioDeviceId
      ? { ...baseAudio, deviceId: { exact: audioDeviceId } }
      : baseAudio
    let audioOutcome = "requested"
    if (videoOnly) {
      // Camera-only acquire (iOS front↔back swap keeps its existing mic capture).
      // Don't request audio at all — re-grabbing the mic while it's live can
      // conflict on iOS. Same pinned→default camera fallback as below.
      audioOutcome = "none"
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video })
      } catch (videoErr) {
        if (!allowDefaultFallback) throw videoErr // swap: keep the user's choice
        console.warn("⚠️ video-only pinned camera failed, retrying default:", videoErr.message)
        stream = await navigator.mediaDevices.getUserMedia({ video: baseVideo })
      }
    } else {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video, audio })
      } catch (mediaErr) {
        // Combined capture failed — the mic may be unavailable, OR the pinned
        // camera id is unusable. Retry video-only WITHOUT the mic pin first
        // (mic is the more likely culprit when the cam id just resolved fine).
        console.warn("⚠️ cam+mic capture failed, retrying video-only:", mediaErr.message)
        stream = null
        // Step 2: if we have a known-good previous mic (different from the one
        // that just failed), try to keep audio alive on that device before
        // giving up on the mic entirely.
        if (prevAudioDeviceId && prevAudioDeviceId !== audioDeviceId) {
          try {
            console.warn("⚠️ retrying with previous mic:", prevAudioDeviceId)
            stream = await navigator.mediaDevices.getUserMedia({
              video,
              audio: { ...baseAudio, deviceId: { exact: prevAudioDeviceId } },
            })
            audioOutcome = "reverted"
          } catch (revertErr) {
            console.warn("⚠️ previous mic also failed, retrying default mic:", revertErr.message)
          }
        }
        // Step 3: no pin — let the browser pick whatever default mic it can.
        if (!stream) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video, audio: baseAudio })
            audioOutcome = "default"
          } catch (defaultAudioErr) {
            console.warn("⚠️ default mic failed, retrying video-only:", defaultAudioErr.message)
          }
        }
        // Step 4: mic is a lost cause — fall back to video-only.
        if (!stream) {
          try {
            stream = await navigator.mediaDevices.getUserMedia({ video })
            audioOutcome = "none"
          } catch (videoErr) {
            if (!allowDefaultFallback) throw videoErr // swap: keep the user's choice
            // The pinned camera itself is unusable (stale/rotated id, or gone) —
            // last resort: the system default camera, no deviceId pin.
            console.warn("⚠️ pinned camera failed, retrying default camera:", videoErr.message)
            stream = await navigator.mediaDevices.getUserMedia({ video: baseVideo })
            audioOutcome = "none"
          }
        }
      }
    }
    // Self-heal the saved device pin: capture what we ACTUALLY opened (the retry
    // chain may have fallen back off the requested id). Label is the stable key;
    // the id can rotate (see resolveDeviceId above).
    const liveVideoTrack = stream.getVideoTracks()[0]
    const liveVideoSettings = liveVideoTrack?.getSettings?.() || {}
    const resolvedVideoId = liveVideoSettings.deviceId || videoDeviceId
    const resolvedVideoLabel = liveVideoTrack?.label || videoLabel
    const liveAudioTrack = stream.getAudioTracks()[0]
    const resolvedAudioId = liveAudioTrack?.getSettings?.().deviceId || audioDeviceId
    const resolvedAudioLabel = liveAudioTrack?.label || audioLabel
    return {
      stream,
      resolvedVideoId,
      resolvedVideoLabel,
      resolvedAudioId,
      resolvedAudioLabel,
      audioOutcome,
      audioDeviceId,
      audioLabel,
    }
  }

  // Route the freshly-acquired raw mic through the vocal chain and hand
  // mediaService a stream whose audio track is the chain's STABLE processed
  // track (video tracks pass through raw). That track's identity never changes
  // across mic device swaps — replaceTracks() sees a same-track replace and
  // watchers keep their audio consumer. The chain owns (and stops) the raw
  // track; mediaService owns the processed one via localStream.
  // keepChain: an iOS video-only swap acquires no audio on purpose — leave the
  // live chain and its mic capture alone instead of detaching.
  const withProcessedAudio = (rawStream, { keepChain = false } = {}) => {
    const rawAudio = rawStream.getAudioTracks()[0]
    if (!rawAudio) {
      if (!keepChain) voiceChain.detachSource()
      return rawStream
    }
    const processed = voiceChain.replaceSource(rawAudio)
    // No Web Audio (or the track couldn't be wrapped): publish raw, feature off.
    if (!processed || processed === rawAudio) return rawStream
    // A rebuilt graph starts from defaults — re-assert the user's settings.
    voiceChain.updateSettings(micProcessingRef.current)
    return new MediaStream([...rawStream.getVideoTracks(), processed])
  }

  // --- Our own webcam (publish) ---
  const handleEnableWebcam = async (opts = {}) => {
    // opts (from the device-setup modal / saved settings): videoDeviceId,
    // audioDeviceId pin specific devices; micOn=false enters with the mic muted.
    // videoLabel/audioLabel let us re-resolve a stale deviceId to the same
    // physical device (see resolveDeviceId).
    const { audioDeviceId, micOn = true, audioLabel } = opts
    try {
      const { stream, resolvedVideoId, resolvedVideoLabel } =
        await acquirePublishStream(opts)
      const publishStream = withProcessedAudio(stream)
      await mediaService.publish(publishStream, outputQuality)
      setIsPublishing(true)
      // Base the merge on the FRESHEST persisted settings, not the deviceSetup
      // state closure — handleSetupConfirm calls saveDeviceSetup() then this
      // publish synchronously, so the state hasn't re-rendered yet and spreading
      // it would clobber the just-saved dontShowAgain/videoOn/micOn back to their
      // previous values. localStorage already has the new object.
      const base = loadDeviceSetup() || deviceSetup
      if (resolvedVideoId && base) {
        // Only rewrite storage if the device pin actually changed (avoid
        // redundant writes and don't touch the preference fields otherwise).
        if (
          base.videoDeviceId !== resolvedVideoId ||
          base.videoLabel !== resolvedVideoLabel
        ) {
          saveDeviceSetup({
            ...base,
            videoDeviceId: resolvedVideoId,
            videoLabel: resolvedVideoLabel,
          })
        }
      }
      // Remember how we published so a reconnect (which wipes the server's SFU
      // state) can transparently re-publish. micOn reflects the resolved state
      // below; videoDeviceId/audioDeviceId pin the same devices (carry labels so
      // a mid-session device-id rotation still re-resolves on republish).
      publishIntentRef.current = {
        videoDeviceId: resolvedVideoId,
        audioDeviceId,
        micOn,
        videoLabel: resolvedVideoLabel,
        audioLabel,
      }
      // Fresh publish: video starts live. If the user chose to enter mic-muted,
      // pause the audio producer immediately (it stays present so they can
      // unmute later via the sidebar toggle, which requires an audio producer).
      setVideoTrackOn(true)
      const hasAudio = publishStream.getAudioTracks().length > 0
      if (hasAudio && micOn === false) {
        try {
          await mediaService.pauseAudio()
        } catch (e) {
          console.warn("⚠️ could not start mic muted:", e.message)
        }
        setMicEnabled(false)
      } else {
        setMicEnabled(hasAudio)
      }
      // Self-monitor uses the LOCAL camera stream directly (lower latency, no
      // SFU round-trip) — it's a preview of your own camera, not the relayed
      // output. Drives the inline sidebar monitor + the grid "You" tile.
      // publishStream (== mediaService.localStream) keeps the bookkeeping
      // coherent; its video track is the same raw camera track either way and
      // the self element is muted, so which audio track rides it is cosmetic.
      setSelfStream(publishStream)
      console.log("🎬 Publishing webcam at quality:", outputQuality)
    } catch (err) {
      console.error("❌ Failed to enable webcam:", err.message)
      setConnectionError("Could not access webcam: " + err.message)
    }
  }
  // Keep the ref pointing at the latest handleEnableWebcam each render, so the
  // memoized connectToChat auto-republishes with current state (outputQuality
  // etc.) rather than a stale closure.
  enableWebcamRef.current = handleEnableWebcam
  // Mirror the live mute state so auto-republish reflects the user's CURRENT
  // choice (see liveMicOnRef).
  liveMicOnRef.current = micEnabled

  // --- Per-track publish toggles (sidebar cam/mic icons) ---
  // These control WHAT is in the live publish: pause/resume the video or audio
  // producer (the SFU stops/starts relaying it to watchers). Distinct from
  // Enable/Disable Webcam, which start/stop the whole publish.
  // Whether we currently have a mic in the publish at all (audio producer
  // exists). When false, the mic toggle is a no-op and "last live track" logic
  // ignores audio.
  const hasMic = () => !!mediaService.getOwnAudioProducerId()

  const handleToggleVideoTrack = async () => {
    if (!mediaService.isPublishing()) return
    const next = !videoTrackOn
    // Turning OFF the last live track ⇒ go fully offline rather than publish a
    // black-and-silent stream. Video is the last track if mic is off/absent.
    if (!next && (!hasMic() || !micEnabled)) {
      await handleDisableWebcam()
      return
    }
    setVideoTrackOn(next) // optimistic
    try {
      if (next) await mediaService.resumeVideo()
      else await mediaService.pauseVideo()
    } catch (err) {
      console.error("❌ Failed to toggle video track:", err.message)
      setVideoTrackOn(!next) // roll back
    }
  }

  const handleToggleMicTrack = async () => {
    if (!hasMic()) return // no mic to toggle
    const next = !micEnabled
    // Turning OFF the last live track ⇒ go fully offline. Mic is the last track
    // if video is already paused off.
    if (!next && !videoTrackOn) {
      await handleDisableWebcam()
      return
    }
    setMicEnabled(next) // optimistic
    try {
      if (next) await mediaService.resumeAudio()
      else await mediaService.pauseAudio()
    } catch (err) {
      console.error("❌ Failed to toggle mic track:", err.message)
      setMicEnabled(!next) // roll back
    }
  }

  const handleDisableWebcam = async () => {
    setSelfStream(null)
    // Intentional stop — don't auto-republish on a later reconnect.
    publishIntentRef.current = null
    await mediaService.unpublish() // also closes a self-watch consumer if open
    // unpublish() stopped the PROCESSED track (it's in localStream); the raw
    // mic is the chain's to release — this turns the OS mic light off.
    voiceChain.detachSource()
    setIsPublishing(false)
    // No one can be watching me once I stop publishing (the server tore down my
    // producer + its consumers); clear the watcher eyes immediately. Also stop
    // watching my own (now-gone) preview so the self tile + its stream go away.
    setWatchedByUsers([])
    setBlockedWatchers([]) // no cam to block anyone from anymore
    if (user?.id) {
      setWatchingUsers((prev) => prev.filter((id) => id !== user.id))
      setConsumedStreams((prev) => {
        const next = { ...prev }
        delete next[user.id]
        return next
      })
    }
    console.log("🛑 Stopped publishing webcam")
  }

  const handleOutputQualityChange = (quality) => {
    setOutputQuality(quality)
    if (mediaService.isPublishing()) mediaService.setOutputQuality(quality)
  }

  const handleWebcamModeChange = (mode) => {
    console.log("Webcam mode change requested:", mode)
    setWebcamMode(mode)
  }

  // Whether the current session is a guest (covers the explicit guest flag, the
  // server role, and the local-fallback guest shape).
  const isGuest =
    authData?.isGuest ||
    authData?.membership?.role === "GUEST" ||
    user?.isGuest ||
    user?.role === "guest"

  // Guest capability gates (tenant flags, default OFF — see
  // utils/guestCaps.js and its backend mirror). These hide affordances only;
  // the server re-checks every one of them, so nothing here is security.
  //
  // `canUploadImages` is one-sided (an upload has no counterparty). Files and
  // private chat are TWO-sided — neither party may be a guest — so those are
  // resolved per target row via canUseCapWith() where the row is known.
  const guestFeatures = tenantConfig?.features
  const canUploadImages = canUseCap(guestFeatures, isGuest, "images")

  // Hot-swap to new devices WHILE already publishing, without dropping watchers.
  // Acquires the new device stream and replaceTrack()s it onto the live
  // producers — the producer ids (and every consumer) survive, so no
  // rtc:producer-closed fires and viewers keep their feed open. Updates only the
  // lightweight publish state (self-monitor stream, persisted pin, republish
  // intent, mic toggle); isPublishing / watched-by stay put. Returns false (so
  // the caller falls back to the disable→enable path) if the swap can't happen,
  // or "kept" (truthy) if the swap failed but the old desktop feed was
  // preserved live — the caller uses this distinct value to revert the
  // persisted camera pin without doing a full republish.
  const handleSwapDevices = async (opts) => {
    // prevAudioDeviceId/prevAudioLabel: the mic that was live BEFORE this swap.
    // They must come from the caller — handleSetupConfirm persists the NEW
    // settings before calling us, so re-loading the saved setup here would
    // just read the new mic back.
    const { audioDeviceId, micOn = true, audioLabel, prevAudioDeviceId, prevAudioLabel } = opts
    // iOS/iPadOS Safari allows only ONE active camera capture at a time. If we
    // hold the current camera track live and call getUserMedia for the other
    // (front↔back) camera, iOS either rejects or silently kills the live track —
    // which used to drop us into the feed-dropping fallback. So on iOS we (a)
    // release ONLY the current camera track first (the mic + producer stay live),
    // and (b) acquire VIDEO-ONLY for the new camera, leaving the existing mic
    // capture and audio producer completely untouched. The producer stays open
    // the whole time, so watchers keep their consumer and never re-watch; a brief
    // black frame on iOS is the only cost. Desktop keeps both cameras live across
    // the swap (no pre-release, full cam+mic re-acquire) so there's not even a
    // flicker there.
    const ios = isIOS()
    // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): trace entry.
    socketService.clientDebugLog("ios-swap", {
      step: "enter",
      ios,
      ua: (navigator.userAgent || "").slice(0, 120),
      reqVideoId: opts?.videoDeviceId,
      reqVideoLabel: opts?.videoLabel,
      publishing: mediaService.isPublishing(),
    })
    if (ios) mediaService.releaseVideoTrackForSwap()
    let stream, resolvedVideoId, resolvedVideoLabel, resolvedAudioId, resolvedAudioLabel, audioOutcome
    try {
      // allowDefaultFallback=false: this is an explicit device switch, so if the
      // chosen camera can't open (e.g. busy — held by OBS/another app) we must
      // NOT silently fall back to the system default camera. acquirePublishStream
      // throws instead, and we recover per-platform below. prevAudioDeviceId
      // rides along in opts for the previous-mic audio fallback.
      ;({ stream, resolvedVideoId, resolvedVideoLabel, resolvedAudioId, resolvedAudioLabel, audioOutcome } =
        await acquirePublishStream({ ...opts, videoOnly: ios, allowDefaultFallback: false }))
    } catch (err) {
      mediaService.clearSwapGuard()
      // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): acquire rejected.
      socketService.clientDebugLog("ios-swap", {
        step: "acquire-failed",
        ios,
        errName: err?.name,
        errMessage: err?.message,
      })
      console.warn("⚠️ swap: chosen camera unavailable:", err.message)
      const busy = err.name === "NotReadableError" || err.name === "TrackStartError"
      const msg = busy
        ? "That camera is in use by another app (e.g. OBS). Close it there first, then try again."
        : "Couldn't switch to that camera — it may be unavailable. Check it's connected and not in use."
      setConnectionError(msg)
      if (!ios) {
        // Desktop: we never released the current camera, so it's STILL LIVE.
        // Keep that feed exactly as-is (don't touch the producer, don't
        // republish) — return "kept" (a distinct truthy value) so
        // handleSetupConfirm does NOT run its disable→re-enable fallback
        // (which would drop the live feed and then re-fail onto the same busy
        // camera, landing on the default anyway), and so it knows to revert
        // the persisted camera pin back to the device that's actually live.
        return "kept"
      }
      // iOS: the old track was already released to free the single camera, so
      // there's no live feed to preserve — let the caller republish to recover.
      throw err
    }
    // Route the new mic through the vocal chain (same-identity processed track
    // → the audio producer replaceTrack is a no-op for watchers). On iOS the
    // acquire was video-only — keepChain leaves the live chain + mic capture
    // untouched, and replaceTracks() splices the carried audio back in below.
    const swapStream = withProcessedAudio(stream, { keepChain: ios })
    let swapped
    try {
      swapped = await mediaService.replaceTracks(swapStream, micOn, /* keepAudio */ ios)
    } catch (rtErr) {
      // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): replaceTracks threw.
      socketService.clientDebugLog("ios-swap", {
        step: "replaceTracks-threw",
        ios,
        errName: rtErr?.name,
        errMessage: rtErr?.message,
      })
      throw rtErr
    }
    if (!swapped) {
      // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): not publishing (race).
      socketService.clientDebugLog("ios-swap", { step: "replaceTracks-false", ios })
      // Not actually publishing video (race) — release this stream and let the
      // caller take the full publish path. Stopping the raw mic fires the
      // chain's 'ended' teardown; the full publish path re-attaches fresh.
      stream.getTracks().forEach((t) => t.stop())
      swapStream.getTracks().forEach((t) => t.stop())
      return false
    }
    // Persist the (possibly re-resolved) device pin, same as a fresh publish.
    const base = loadDeviceSetup() || deviceSetup
    if (base) {
      let updated = null
      if (resolvedVideoId &&
          (base.videoDeviceId !== resolvedVideoId || base.videoLabel !== resolvedVideoLabel)) {
        updated = { ...(updated || base), videoDeviceId: resolvedVideoId, videoLabel: resolvedVideoLabel }
      }
      // On iOS the mic was never touched by this swap (videoOnly acquire) — skip
      // audio pin edits entirely; only desktop re-acquires audio here.
      if (!ios) {
        switch (audioOutcome) {
          case "requested":
            // Mirror the video self-heal: capture whatever id/label actually opened.
            if (resolvedAudioId &&
                (base.audioDeviceId !== resolvedAudioId || base.audioLabel !== resolvedAudioLabel)) {
              updated = { ...(updated || base), audioDeviceId: resolvedAudioId, audioLabel: resolvedAudioLabel }
            }
            break
          case "reverted":
            // Chosen mic failed; we kept the previous mic alive instead.
            updated = {
              ...(updated || base),
              audioDeviceId: prevAudioDeviceId,
              audioLabel: prevAudioLabel,
            }
            setConnectionError("Couldn't switch to that microphone — kept your previous mic.")
            break
          case "default":
            // Chosen mic failed; browser picked its own default mic instead.
            updated = { ...(updated || base), audioDeviceId: resolvedAudioId, audioLabel: resolvedAudioLabel }
            if (opts.audioDeviceId) {
              setConnectionError("Couldn't switch to that microphone — using your system default mic.")
            }
            break
          case "none":
            // No mic could be opened at all.
            updated = { ...(updated || base), audioDeviceId: "", audioLabel: "" }
            if (opts.audioDeviceId) {
              setConnectionError("Couldn't open that microphone — your mic is now off.")
            }
            break
          default:
            break
        }
      }
      if (updated) saveDeviceSetup(updated)
    }
    // A fully-successful swap (or an iOS video-only swap, where the mic was
    // never touched) clears any lingering device-switch banner from an earlier
    // failed attempt — but ONLY our own messages, never unrelated errors.
    if (ios || audioOutcome === "requested") {
      setConnectionError((prev) =>
        prev &&
        /^(That camera is in use|Couldn't (switch to that (camera|microphone)|open that microphone))/.test(prev)
          ? null
          : prev
      )
    }
    publishIntentRef.current = {
      videoDeviceId: resolvedVideoId,
      audioDeviceId,
      micOn,
      videoLabel: resolvedVideoLabel,
      audioLabel,
    }
    // Reflect the resolved mic state (the new device may have no mic at all).
    // swapStream is what mediaService adopted as localStream — on an iOS
    // keepAudio swap it had the still-live audio track spliced into it inside
    // replaceTracks(), so its audio presence is the source of truth.
    setMicEnabled(swapStream.getAudioTracks().length > 0 && micOn !== false)
    // Self-monitor follows the new local stream.
    setSelfStream(swapStream)
    // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): hot-swap succeeded.
    socketService.clientDebugLog("ios-swap", {
      step: "success",
      ios,
      resolvedVideoId,
      resolvedVideoLabel,
    })
    console.log("🔄 Hot-swapped camera/mic — watchers kept their feed")
    return true
  }

  // Apply the user's choices from the device-setup modal: persist them, close
  // the modal, then publish (or stop) accordingly. When opened via the gear
  // while already publishing, HOT-SWAP the live producers to the new devices via
  // replaceTrack so watchers never lose the feed. Falls back to a full
  // stop→restart if the hot-swap fails for any reason.
  const handleSetupConfirm = (settings) => {
    const prevSetup = loadDeviceSetup() || deviceSetup
    saveDeviceSetup(settings)
    setShowSetupModal(false)
    setSetupReopened(false)
    autoEnabledRef.current = true // an explicit choice supersedes auto-start
    const opts = {
      videoDeviceId: settings.videoDeviceId,
      audioDeviceId: settings.audioDeviceId,
      videoLabel: settings.videoLabel,
      audioLabel: settings.audioLabel,
      micOn: settings.micOn !== false,
      // The pre-confirm setup (saveDeviceSetup above already overwrote the
      // stored one) — handleSwapDevices falls back to / reverts to this mic
      // when the newly chosen one can't be opened.
      prevAudioDeviceId: prevSetup?.audioDeviceId,
      prevAudioLabel: prevSetup?.audioLabel,
    }
    ;(async () => {
      if (settings.videoOn) {
        if (mediaService.isPublishing()) {
          // Already live → swap in place (seamless for viewers). If the swap
          // throws, fall back to the old stop-then-restart so the user still
          // ends up on the new camera (viewers re-watch, as before).
          try {
            const ok = await handleSwapDevices(opts)
            if (ok === "kept" && prevSetup) {
              // Swap failed entirely (acquire threw) but the old camera+mic are
              // still live — revert BOTH persisted pins so the saved devices
              // match reality (dropdown + next session).
              saveDeviceSetup({
                ...settings,
                videoDeviceId: prevSetup.videoDeviceId,
                videoLabel: prevSetup.videoLabel,
                audioDeviceId: prevSetup.audioDeviceId,
                audioLabel: prevSetup.audioLabel,
              })
            }
            if (ok) return
            // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): swap returned
            // false → falling through to the full republish (drops watchers).
            socketService.clientDebugLog("ios-swap", { step: "fallback-republish", reason: "returned-false" })
          } catch (err) {
            // TEMP DIAGNOSTIC (remove once iOS swap bug pinned): swap threw →
            // full republish (drops watchers).
            socketService.clientDebugLog("ios-swap", { step: "fallback-republish", reason: "threw", errMessage: err?.message })
            console.warn("⚠️ hot-swap failed, falling back to republish:", err.message)
          }
          await handleDisableWebcam()
        }
        await handleEnableWebcam(opts)
      } else if (mediaService.isPublishing()) {
        // Turned the camera off in the modal while live → stop publishing.
        await handleDisableWebcam()
      }
    })()
  }

  // A mic-processing pref changed (Settings → Voice & Video → Microphone
  // processing). Persist + live-apply. Chain params (gain/low-cut/compressor/
  // gate) take effect through the running graph with no re-publish; the EC/NS
  // toggles are getUserMedia CONSTRAINTS, so while live they need a mic
  // re-acquire via the hot-swap path (watchers keep their consumer — the
  // processed track's identity never changes).
  const handleMicProcessingChange = (partial) => {
    const next = { ...micProcessingRef.current, ...partial }
    setMicProcessing(next)
    const key = micProcessingStorageKey()
    if (key) {
      try {
        localStorage.setItem(key, JSON.stringify(next))
      } catch {
        /* private mode / quota */
      }
    }
    voiceChain.updateSettings(partial)
    // Future server-backed persistence slots in here (view-mode pattern):
    // socketService.setMicProcessing(next)
    const touchesCapture =
      "disableEchoCancellation" in partial || "disableNoiseSuppression" in partial
    // iOS: re-grabbing the live mic conflicts with the single-capture rule
    // (see handleSwapDevices) — the new constraints apply on the next publish.
    if (touchesCapture && mediaService.isPublishing() && !isIOS()) {
      const intent = publishIntentRef.current
      if (intent) {
        handleSwapDevices({
          videoDeviceId: intent.videoDeviceId,
          audioDeviceId: intent.audioDeviceId,
          videoLabel: intent.videoLabel,
          audioLabel: intent.audioLabel,
          micOn: liveMicOnRef.current !== false,
          prevAudioDeviceId: intent.audioDeviceId,
          prevAudioLabel: intent.audioLabel,
        }).catch((err) =>
          console.warn("⚠️ mic re-acquire after EC/NS change failed:", err.message)
        )
      }
    }
  }

  // Dismiss the modal without applying (enter with the webcam off). Suppress the
  // auto-start so an explicit skip doesn't immediately turn the camera on.
  const handleSetupClose = () => {
    setShowSetupModal(false)
    setSetupReopened(false)
    autoEnabledRef.current = true
  }

  // Reopen the setup modal on demand (gear icon on the webcam panel). Flag it as
  // a reopen so the dialog shows "Cancel" rather than "Skip".
  const handleOpenDeviceSetup = () => {
    setSetupReopened(true)
    setShowSetupModal(true)
  }

  // Decide, once per entry, whether to show the setup modal. Guests always see
  // it; members see it until they've checked "don't show again".
  useEffect(() => {
    if (setupDecidedRef.current) return
    if (!isAuthenticated || !authData || !tenantInfo) return
    setupDecidedRef.current = true
    const saved = loadDeviceSetup()
    setDeviceSetup(saved)
    if (isGuest || !saved?.dontShowAgain) {
      setShowSetupModal(true)
    }
    // Suppressed members auto-start via the connect-gated effect below.
  }, [isAuthenticated, authData, tenantInfo, isGuest, loadDeviceSetup])

  // On a suppressed entry (modal not shown) with a saved camera-on preference,
  // auto-start the webcam once the socket/media connection is established.
  useEffect(() => {
    if (autoEnabledRef.current) return
    if (!setupDecidedRef.current || showSetupModal) return
    if (!hasConnectedAfterAuth) return
    const saved = deviceSetup
    if (saved?.dontShowAgain && saved?.videoOn && !mediaService.isPublishing()) {
      autoEnabledRef.current = true
      handleEnableWebcam({
        videoDeviceId: saved.videoDeviceId,
        audioDeviceId: saved.audioDeviceId,
        videoLabel: saved.videoLabel,
        audioLabel: saved.audioLabel,
        micOn: saved.micOn !== false,
      })
    }
    // handleEnableWebcam is stable enough for this one-shot guarded call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasConnectedAfterAuth, showSetupModal, deviceSetup])

  // Get participants for the video grid: every user I'm watching, including
  // myself. Every grid tile — self included — shows the SFU-consumed stream
  // (post-encode, what others actually receive); watching yourself is no
  // different from watching anyone else here. (The inline sidebar self-monitor
  // is the separate LOW-latency local-camera preview, not this.) Self sorts
  // first.
  const getWatchedParticipants = () => {
    // Order tiles by the order cams were opened, NOT by user-list order.
    // watchingUsers is appended on each accepted watch, so it already holds
    // ids in open order; map over it (resolving each to its onlineUsers entry)
    // rather than iterating onlineUsers, which would impose user-list order.
    const tiles = watchingUsers
      .map((id) => onlineUsers.find((u) => u.id === id))
      .filter((onlineUser) => onlineUser && onlineUser.isOnline)
      .map((onlineUser) => {
        const isSelf = onlineUser.id === user?.id
        // Video paused: for self it's our local toggle; for others it's driven
        // by their rtc:producer-paused. A paused tile shows a placeholder.
        const videoPaused = isSelf
          ? !videoTrackOn
          : pausedVideoUsers.includes(onlineUser.id)
        return {
          id: onlineUser.id,
          username: isSelf ? "You" : onlineUser.username,
          isSelf,
          hasVideo: !!consumedStreams[onlineUser.id],
          stream: consumedStreams[onlineUser.id] || null,
          videoPaused,
          isSpeaking: false,
          volumeLevel: 0,
          // Listening-chain volume (0-400) for the tile's speaker-icon state.
          // The prefs mirror updates on every popover change; getSettings is
          // the cold-start read (persisted value before any interaction).
          remoteVolume: isSelf
            ? null
            : (remoteAudioPrefs[onlineUser.id] || remoteAudio.getSettings(onlineUser.id)).volume,
          // The video producer id — lets the tile's "stats for nerds" panel
          // resolve the mediasoup consumer for getStats(). Self reads its own
          // producer via mediaService regardless, so this is only required for
          // others' tiles.
          producerId: isSelf
            ? mediaService.getOwnProducerId()
            : producersByUser[onlineUser.id],
        }
      })

    // Ordering:
    //  - No manual arrangement yet (tileOrder empty): default = self first,
    //    then everyone else in open order (stable sort preserves it).
    //  - Manual arrangement set: sort by the user's tileOrder positions.
    //    Tiles present in tileOrder keep the exact slot the user dragged them
    //    to (self included — a manual drag overrides self-first). Newly-opened
    //    cams not yet in tileOrder land at the END, keeping their open order.
    if (tileOrder.length === 0) {
      tiles.sort((a, b) => (a.isSelf === b.isSelf ? 0 : a.isSelf ? -1 : 1))
    } else {
      const rank = (id) => {
        const i = tileOrder.indexOf(id)
        return i === -1 ? Number.MAX_SAFE_INTEGER : i
      }
      tiles.sort((a, b) => rank(a.id) - rank(b.id))
    }
    return tiles
  }

  // Drag-to-reorder: move the dragged tile to the slot occupied by the tile it
  // was dropped on. Seeds tileOrder from the CURRENT displayed order the first
  // time (so the arrangement starts from what the user sees, including any
  // newly-opened tiles that were only implicitly ordered), then applies the
  // move. No-op if a tile is dropped on itself.
  const handleReorderTiles = (draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId) return
    setTileOrder((prev) => {
      const base =
        prev.length > 0 ? [...prev] : getWatchedParticipants().map((p) => p.id)
      // Ensure both ids are present (a brand-new cam may be missing from prev).
      for (const id of [draggedId, targetId]) {
        if (!base.includes(id)) base.push(id)
      }
      const from = base.indexOf(draggedId)
      const to = base.indexOf(targetId)
      if (from === -1 || to === -1) return prev
      base.splice(from, 1)
      base.splice(to, 0, draggedId)
      return base
    })
  }

  const handleWindowOut = (userId) => {
    console.log('Popping out user to separate window:', userId)
    setPoppedOutUsers((prev) => {
      if (prev.includes(userId)) {
        return prev
      }
      return [...prev, userId]
    })
  }

  const handleWindowIn = (userId) => {
    console.log('Popping in user from separate window:', userId)
    setPoppedOutUsers((prev) => prev.filter((id) => id !== userId))
  }

  // Show loading or error if tenant info is not available
  if (!tenantInfo) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#0f1115",
            color: "#ffffff",
          }}
        >
          Loading chat instance...
        </div>
      </ThemeProvider>
    )
  }

  // Show auth screen if not authenticated
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthScreen
          onLogin={handleLogin}
          onGuestLogin={handleGuestLogin}
          tenantConfig={tenantConfig}
        />
      </ThemeProvider>
    )
  }

  const watchedParticipants = getWatchedParticipants()
  const hasWebcams = watchedParticipants.length > 0

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div
        className="app-root"
        style={{
          fontFamily: theme.palette.custom.fontFamily,
          // Expose theme accents to global CSS (App.css: splitter hover, focus
          // ring, text selection) so they repaint with the theme too.
          "--accent": theme.palette.primary.main,
          "--accent-wash": theme.palette.custom.accentWash,
        }}
      >
        {/* Hidden file picker for peer-to-peer "Send File" (opened from the
            RightSidebar row menu). Always mounted so the ref is available. */}
        <input
          ref={fileSendInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleFilePicked}
        />

        {/* Fixed Top Bar */}
        <div className="app-bar">
          <TopBar
            user={user}
            roomName={currentRoom}
            onMenuClick={handleMenuClick}
            onLogout={handleLogout}
            role={authData?.membership?.role}
            isGuest={authData?.isGuest}
            displayName={authData?.membership?.username || user?.displayName || user?.username}
            themeName={themeName}
            onThemeChange={handleThemeChange}
            usernameColor={usernameColor}
            canPickUsernameColor={!authData?.isGuest && authData?.membership?.role !== 'GUEST'}
            canUseReservedColor={['ADMIN', 'OWNER'].includes(authData?.membership?.role)}
            onUsernameColorChange={handleUsernameColorChange}
            outputQuality={outputQuality}
            canPickOutputQuality={!authData?.isGuest && authData?.membership?.role !== 'GUEST'}
            onOutputQualityChange={handleOutputQualityChange}
            mentionSound={mentionSound}
            onMentionSoundChange={handleMentionSoundChange}
            mentionVolume={mentionVolume}
            onMentionVolumeChange={handleMentionVolumeChange}
            onMentionVolumePreview={handleMentionVolumePreview}
            fontSize={fontSize}
            onFontSizeChange={handleFontSizeChange}
            timestampFormat={timestampFormat}
            onTimestampFormatChange={handleTimestampFormatChange}
            imMode={imMode}
            onImModeChange={handleImModeChange}
            imClassic={imClassic}
            onImClassicChange={handleImClassicChange}
            awayMessage={awayMessage}
            onAwayChange={handleAwayChange}
            deviceSetup={deviceSetup}
            onDeviceSetupChange={handleSetupConfirm}
            micProcessing={micProcessing}
            onMicProcessingChange={handleMicProcessingChange}
            isPublishing={isPublishing}
            onAvatarChange={handleAvatarChange}
            onAvatarPick={handleAvatarPick}
            bio={profileBio}
            onBioChange={handleBioChange}
            bannerColor={profileBannerColor}
            onBannerColorChange={handleBannerColorChange}
            quickReplies={quickReplies}
            onQuickRepliesChange={handleQuickRepliesChange}
            onStartSesh={handleStartSesh}
            onJoinSesh={handleJoinSesh}
            canStartSesh={!isGuest}
            currentUserId={user?.id}
            sesh={sesh}
            seshSynced={seshSynced}
          />
        </div>

        {/* Read-only profile card for another user (opened from a user-row
            "Profile" action). Shows identity + usage stats; stats area is gated
            by the tenant's stats feature. */}
        <ProfileCard
          open={!!profileCardUser}
          onClose={() => setProfileCardUser(null)}
          targetUser={profileCardUser || {}}
        />

        {/* AIM-style IM windows are rendered by the WindowLayer (a portal to
            document.body) — see windowing/. A received IM pops a managed window
            even if the recipient hasn't turned IM mode on (that IS the instant-
            messenger behavior); the IM-mode toggle only governs the OUTGOING
            affordance. The layer also renders the minimized-window taskbar. */}
        <WindowLayer />
        {/* Bridge: clears a conversation's unread/blink when its window gains
            focus (the manager owns focus; this watches it). */}
        <ImFocusBridge onFocusedChange={handleImWindowFocused} />

        {/* Main Content Area */}
        {/* ComposerProvider lets the RightSidebar row menu's "Mention" insert
            into the chat composer without prop-drilling a ref through here. */}
        <ComposerProvider>
        <div className="main-content">
          {/* Left Content - Video + Chat */}
          {/* Chat and user container */}
          <div className="left-content">
            <div id="container">
              {hasWebcams ? (
                /* When webcams are being watched, show a resizable split with the
                   video grid on top and the chat below. */
                <ReflexContainer orientation="horizontal">
                  <ReflexElement
                    className="video-section"
                    minSize={200}
                    size={300}
                  >
                    <VideoGrid
                      participants={watchedParticipants}
                      poppedOutUsers={poppedOutUsers}
                      onReorder={handleReorderTiles}
                      onCloseWebcam={handleUnwatchUser}
                      onWindowOut={handleWindowOut}
                      onViewQualityChange={handleViewQualityChange}
                      statsOpenUsers={statsOpenUsers}
                      statsAnchors={statsAnchorsRef.current}
                      statsPositions={statsPosRef.current}
                      onToggleStats={handleToggleStats}
                      onCloseStats={handleCloseStats}
                      onStatsPositionChange={handleStatsPositionChange}
                      onOpenVolume={handleOpenVolume}
                      noodledUserId={noodleShame?.userId || null}
                      noodleTrack={noodleTrack}
                    />
                  </ReflexElement>

                  <ReflexSplitter />

                  <ReflexElement className="chat-section" minSize={250}>
                    <ChatPanel
                      messages={messages}
                      onSendMessage={handleSendMessage}
                      onSendImage={handleSendImage}
                      canUploadImages={canUploadImages}
                      onTyping={handleTyping}
                      onlineUsers={onlineUsers}
                      currentUserId={user?.id}
                      isConnected={socketService.isConnected}
                      connectionError={connectionError}
                      isConnecting={isConnecting}
                      pendingMessageCount={pendingMessageCount}
                      currentColor={currentColor}
                      onColorChange={handleColorChange}
                      viewMode={currentViewMode}
                      onViewModeChange={handleViewModeChange}
                      fontSize={fontSize}
                      timestampFormat={timestampFormat}
                      pendingCamRequests={pendingCamRequests}
                      onAcceptCamRequest={handleAcceptCamRequest}
                      onDenyCamRequest={handleDenyCamRequest}
                      pendingCamInvites={pendingCamInvites}
                      onAcceptCamInvite={handleAcceptCamInvite}
                      onDenyCamInvite={handleDenyCamInvite}
                      onIgnoreCamInvite={handleIgnoreCamInvite}
                      pendingFileOffers={pendingFileOffers}
                      onAcceptFileOffer={handleAcceptFileOffer}
                      onDenyFileOffer={handleDenyFileOffer}
                      fileTransfers={fileTransfers}
                      noodledUserId={noodleShame?.userId || null}
                      onOpenUserMenu={handleOpenUserMenu}
                      ignoredUserIds={ignoredUserIds}
                      quickReplies={quickReplies}
                      onQuickRepliesChange={handleQuickRepliesChange}
                    />
                  </ReflexElement>
                </ReflexContainer>
              ) : (
                /* No webcams: let chat take the full height — no wasted video
                   strip or splitter, and the input stays pinned to the bottom. */
                <div className="chat-section">
                  <ChatPanel
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    onSendImage={handleSendImage}
                    canUploadImages={canUploadImages}
                    onTyping={handleTyping}
                    onlineUsers={onlineUsers}
                    currentUserId={user?.id}
                    isConnected={socketService.isConnected}
                    connectionError={connectionError}
                    isConnecting={isConnecting}
                    pendingMessageCount={pendingMessageCount}
                    currentColor={currentColor}
                    onColorChange={handleColorChange}
                    viewMode={currentViewMode}
                    onViewModeChange={handleViewModeChange}
                    fontSize={fontSize}
                    timestampFormat={timestampFormat}
                    pendingCamRequests={pendingCamRequests}
                    onAcceptCamRequest={handleAcceptCamRequest}
                    onDenyCamRequest={handleDenyCamRequest}
                    pendingCamInvites={pendingCamInvites}
                    onAcceptCamInvite={handleAcceptCamInvite}
                    onDenyCamInvite={handleDenyCamInvite}
                    onIgnoreCamInvite={handleIgnoreCamInvite}
                    pendingFileOffers={pendingFileOffers}
                    onAcceptFileOffer={handleAcceptFileOffer}
                    onDenyFileOffer={handleDenyFileOffer}
                    fileTransfers={fileTransfers}
                    noodledUserId={noodleShame?.userId || null}
                    onOpenUserMenu={handleOpenUserMenu}
                    ignoredUserIds={ignoredUserIds}
                    quickReplies={quickReplies}
                    onQuickRepliesChange={handleQuickRepliesChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <RightSidebar
            onlineUsers={onlineUsers}
            watchingUsers={watchingUsers}
            watchedByUsers={watchedByUsers}
            blockedWatchers={blockedWatchers}
            blockedByUsers={blockedByUsers}
            deniedCooldownUsers={deniedCooldownUsers}
            onWatchUser={handleWatchUser}
            onUnwatchUser={handleUnwatchUser}
            onToggleWatchPermission={handleToggleWatchPermission}
            onOpenUserMenu={handleOpenUserMenu}
            friendIds={friendIds}
            incomingRequestIds={incomingRequestIds}
            friendsEnabled={tenantConfig?.features?.friends !== false}
            producersByUser={producersByUser}
            pausedVideoUsers={pausedVideoUsers}
            currentUserId={user?.id}
            currentUserRole={authData?.membership?.role}
            isGuest={isGuest}
            isPublishing={isPublishing}
            selfStream={selfStream}
            videoTrackOn={videoTrackOn}
            micEnabled={micEnabled}
            onToggleVideoTrack={handleToggleVideoTrack}
            onToggleMicTrack={handleToggleMicTrack}
            onEnableWebcam={() =>
              handleEnableWebcam(
                deviceSetup
                  ? {
                      videoDeviceId: deviceSetup.videoDeviceId,
                      audioDeviceId: deviceSetup.audioDeviceId,
                      videoLabel: deviceSetup.videoLabel,
                      audioLabel: deviceSetup.audioLabel,
                      micOn: deviceSetup.micOn !== false,
                    }
                  : {}
              )
            }
            onDisableWebcam={handleDisableWebcam}
            onWebcamModeChange={handleWebcamModeChange}
            onOpenDeviceSetup={handleOpenDeviceSetup}
            noodledUserId={noodleShame?.userId || null}
          />
        </div>

        {/* The ONE shared user context menu — summoned from sidebar rows and
            chat usernames alike. Must render inside ComposerProvider: its
            "Mention" item inserts into the composer via useComposer(). */}
        <UserContextMenu
          open={Boolean(userMenu && userMenuUser)}
          position={userMenu?.position}
          user={userMenuUser}
          onClose={() => setUserMenu(null)}
          currentUserRole={authData?.membership?.role}
          isPublishing={isPublishing}
          watchingUsers={watchingUsers}
          blockedWatchers={blockedWatchers}
          blockedByUsers={blockedByUsers}
          friendIds={friendIds}
          incomingRequestIds={incomingRequestIds}
          outgoingRequestIds={outgoingRequestIds}
          ignoredUserIds={ignoredUserIds}
          friendsEnabled={tenantConfig?.features?.friends !== false}
          canPrivateChatWith={canUseCapWith(
            guestFeatures, isGuest, userMenuUser?.role, "privateChat"
          )}
          canSendFileTo={canUseCapWith(
            guestFeatures, isGuest, userMenuUser?.role, "files"
          )}
          onWatchToggle={(userId) =>
            watchingUsers.includes(userId)
              ? handleUnwatchUser(userId)
              : handleWatchUser(userId)
          }
          onToggleWatchPermission={handleToggleWatchPermission}
          onInviteUser={handleInviteUser}
          onOpenVolume={handleOpenVolume}
          onRowAction={handleRowMenuAction}
        />
        <VolumePopover
          open={!!volumePopover}
          anchorEl={volumePopover?.anchorEl || null}
          anchorPosition={volumePopover?.anchorPosition || null}
          username={
            volumePopover
              ? onlineUsers.find((u) => u.id === volumePopover.userId)?.username || ""
              : ""
          }
          volume={
            volumePopover
              ? (remoteAudioPrefs[volumePopover.userId] ||
                  remoteAudio.getSettings(volumePopover.userId)).volume
              : 100
          }
          compressorOn={
            volumePopover
              ? (remoteAudioPrefs[volumePopover.userId] ||
                  remoteAudio.getSettings(volumePopover.userId)).compressor
              : false
          }
          onVolumeChange={(pct) =>
            volumePopover && handleRemoteVolumeChange(volumePopover.userId, pct)
          }
          onCompressorToggle={() =>
            volumePopover && handleRemoteCompressorToggle(volumePopover.userId)
          }
          onClose={() => setVolumePopover(null)}
        />
        </ComposerProvider>
        {poppedOutUsers.map((userId, index) => {
          console.log('Rendering popped out window for user:', userId)
          const participant = watchedParticipants.find(
            (p) => p.id === userId
          );

          if (!participant) {
            return null;
          }

          return (
            <Rnd
              key={userId}
              bounds="parent"
              default={{
                x: 50 + index * 30,
                y: 50 + index * 30,
                width: 400,
                height: 300,
              }}
              className="rnd-window"
            >
              <VideoParticipantTile
                key={participant.id}
                participant={participant}
                // classes={classes}
                noodled={!!noodleShame && participant.id === noodleShame.userId}
                noodleTrack={
                  noodleTrack && noodleTrack.userId === participant.id ? noodleTrack : null
                }
                viewQuality={'default'}
                // onPrivateRequest={handlePrivateRequest}
                // onQualityChange={handleQualityChange}
                // onWindowOut={handleWindowOut}
                onWindowIn={handleWindowIn}
                onClose={handleUnwatchUser}
                statsOpen={statsOpenUsers.includes(participant.id)}
                statsAnchor={statsAnchorsRef.current[participant.id]}
                statsPos={statsPosRef.current[participant.id]}
                onToggleStats={handleToggleStats}
                onCloseStats={handleCloseStats}
                onStatsPositionChange={handleStatsPositionChange}
                onOpenVolume={handleOpenVolume}
                remoteVolume={participant.remoteVolume}
                />
            </Rnd>
          );
        })}

        {/* Group "Sesh" Countdown status is shown inline in the TopBar (right
            of the logo) — see TopBar's sesh status block. The old floating
            SeshBanner is intentionally not rendered. */}

        {/* Auto-reconnect modal — overlays the chat shell (which stays mounted,
            preserving messages/state) and blocks input while we back off. */}
        <ReconnectModal
          reconnect={reconnect}
          maxAttempts={RECONNECT_DELAYS.length}
          maintenance={isMaintenance}
          onRetry={retryReconnect}
          onLeave={leaveReconnect}
        />

        {/* Terminal "you were kicked by an admin" notice. Overlays the chat
            shell (which stays mounted behind it); dismissing returns home. */}
        <KickedModal kickedBy={kickedBy} onDismiss={handleKickedDismiss} />

        {/* AFK idle policy. The warning is a NON-blocking banner — a scrim would
            block the very activity that clears it. The kicked screen does block:
            that session is genuinely over, but the login is kept so Rejoin is
            one click. */}
        <IdleWarningBanner
          deadline={idleDeadline}
          onStillHere={handleStillHere}
          confirming={idleConfirming}
        />
        <IdleKickedScreen
          open={idleKicked}
          onRejoin={handleIdleRejoin}
          onLogout={handleLogout}
        />

        {/* Pre-join device/stream setup. Shown on entry (always for guests;
            until "don't show again" for members) and reopenable via the webcam
            panel gear. */}
        <DeviceSetupModal
          open={showSetupModal}
          onClose={handleSetupClose}
          onConfirm={handleSetupConfirm}
          isGuest={isGuest}
          initialSettings={deviceSetup}
          showDontShowAgain={!isGuest}
          reopened={setupReopened}
          isPublishing={isPublishing}
          activeStream={selfStream}
          micProcessing={micProcessing}
          onMicProcessingChange={handleMicProcessingChange}
        />
      </div>
    </ThemeProvider>
  )
}

// App is wrapped in the IM window-manager provider so AppInner can use the
// imperative window API (open/close/focus/update IM windows) and the
// WindowLayer (rendered inside AppInner) can read window state. The manager is
// IM-only for now but lives as its own generic module under windowing/.
function App() {
  return (
    <WindowManagerProvider>
      <AppInner />
    </WindowManagerProvider>
  )
}

export default App
